// [SCOPE 051 demo / 053] Authored de-DE bundle.
//
// Demonstrates the localization framework end-to-end: the keys translated here
// render in German when the locale is de-DE; any key NOT present falls back to
// en-US per key (FR-002). Currently scoped to the landing page (the wired slice)
// plus common/settings; the full app-wide extraction is spec 052.
//
// Methodology/lifecycle terms and brand/compliance names (wxKanban, SOC 2, HIPAA,
// GDPR, HITRUST) are intentionally left untranslated.
import type { PartialLocaleBundle } from "./en-US";

export const deDE: PartialLocaleBundle = {
  common: {
    save: "Speichern",
    cancel: "Abbrechen",
    close: "Schließen",
    delete: "Löschen",
    edit: "Bearbeiten",
    loading: "Wird geladen…",
    saving: "Wird gespeichert…",
    saved: "Gespeichert",
    error: "Etwas ist schiefgelaufen",
    retry: "Erneut versuchen",
    yes: "Ja",
    no: "Nein",
  },
  settings: {
    language: {
      title: "Sprache",
      description: "Wählen Sie die Sprache für die App-Oberfläche.",
      help: "Nicht übersetzte Sprachen verwenden Englisch, bis ihre Texte verfasst sind.",
      saved: "Sprache aktualisiert.",
    },
  },
  landing: {
    nav: {
      home: "Startseite",
      features: "Funktionen",
      pricing: "Preise",
      docs: "Hilfe/Tutorials",
      signIn: "Anmelden",
    },
    hero: {
      subtitle: "Projektmanagement-System mit KI",
      description:
        "wxKanban ist ein umfassendes, rund um KI entwickeltes Projektmanagement-System mit einem produktionsreifen Starter-Kit für Teams jeder Größe. Entwickelt für Sicherheit, Compliance und nahtlose Integrationen.",
      getStarted: "Loslegen",
      learnMore: "Mehr erfahren",
    },
    install: {
      eyebrow: "In ~30 Minuten startklar",
      heading: "Installation & Einrichtung",
      description:
        "Neu bei wxKanban? Unsere Schritt-für-Schritt-Anleitung bringt Sie von einem frischen Computer zu einer funktionierenden Einrichtung in VS Code — Claude Code installieren, das Dev Cockpit hinzufügen und Ihr erstes Projekt einrichten. Keine Vorkenntnisse erforderlich.",
      step1: {
        title: "Die Tools installieren",
        body: "VS Code, Node.js und Claude Code — mit Befehlen zum Kopieren und Einfügen.",
      },
      step2: {
        title: "Das Dev Cockpit hinzufügen",
        body: "Installieren Sie die Erweiterung und verbinden Sie sie mit Ihrem wxKanban-Projekt.",
      },
      step3: {
        title: "Ihren ersten Workflow ausführen",
        body: "Scope, Spec und Build — geführt durch den Lebenszyklus mit 6 Phasen.",
      },
      cta: "Einrichtungsanleitung ansehen",
    },
    features: {
      heading: "Leistungsstarke Funktionen",
      subheading:
        "Alles, was Sie brauchen, um Projekte zu verwalten, Zeit zu erfassen und mit Ihrem Team zusammenzuarbeiten",
      aiPowered: {
        title: "KI-gestützt",
        description:
          "Intelligente Aufgabenvorschläge und Workflow-Optimierung, angetrieben von Ihrem bevorzugten KI-Tool für die Entwicklung",
      },
      security: {
        title: "Sicherheit für Unternehmen",
        description: "Konform mit SOC 2, HIPAA und DSGVO, mit AES-256-GCM-Verschlüsselung",
      },
      integration: {
        title: "Integration mehrerer Systeme",
        description:
          "Verbinden Sie Jira, Monday.com, Asana, Trello und andere Projektmanagement-Plattformen nahtlos",
      },
      conversion: {
        title: "Konvertierung von Alt-Anwendungen",
        intro: "Konvertieren Sie Ihre Alt-Anwendungen wie:",
        more: "und mehr",
      },
      collaboration: {
        title: "Team-Zusammenarbeit",
        item1:
          "Team-Verwaltung in Echtzeit mit rollenbasierter Zugriffskontrolle",
        item2:
          "Fügen Sie die SDLC-Dokumente Ihres Unternehmens hinzu, um sie auf Compliance zu prüfen",
        item3:
          "Weisen Sie jedem Teammitglied den SDLC zu, um die Audit-Kontrollen einzuhalten",
        item4:
          "Lassen Sie sich von der KI helfen, alle Audit-Anforderungen zu erfüllen",
      },
      lifecycle: {
        title: "Lebenszyklus mit 6 Phasen",
        item1: "Strukturierte Qualitätsmethodik, von Design bis Release",
        item2:
          "Open-Source-Befehle und Claude, um den gesamten Entwicklungsprozess zu steuern",
        item3:
          "Aktualisieren Sie specs und Aufgaben in Ihrem Projektmanagement-System automatisch",
        item4: "Automatische Abläufe zur Sicherstellung der Audit-Compliance",
        item5: "Vollständiger Audit-Bericht bei jeder Version",
        footer:
          "Was Sie in VS Code schreiben und generieren – jetzt in einem vollwertigen Projektmanagement-System",
      },
    },
    pricing: {
      heading: "Einfache, transparente Preise",
      subheading:
        "Wählen Sie den perfekten Plan für Ihr Team. Immer mit der Flexibilität, hoch- oder herabzustufen",
      mostPopular: "AM BELIEBTESTEN",
      perMonth: "/Monat",
      free: "Kostenlos",
      faqText: "Haben Sie Fragen zu den Preisen?",
      contactSales: "Kontaktieren Sie unser Vertriebsteam",
      tiers: {
        single: {
          name: "Einzelnutzer",
          userLimit: "1 Nutzer",
          f1: "Unbegrenzte Projekte",
          f2: "Eine Projektmanagement-Integration",
          f3: "E-Mail-Support",
          cta: "Jetzt starten",
        },
        smb: {
          name: "Kleine/Mittlere Unternehmen",
          userLimit: "Bis zu 5 Nutzer",
          f1: "SOC 2- und HIPAA-zertifiziert",
          f2: "Ein einzelnes Unternehmen",
          f3: "Team-Verwaltung",
          f4: "E-Mail-Support",
          cta: "Jetzt starten",
        },
        consultant: {
          name: "Berater",
          userLimit: "Unbegrenztes Team",
          f1: "Unbegrenzte Teammitglieder",
          f2: "Mehrere Projektmanagement-Verbindungen",
          f3: "Erweiterte Zeiterfassung",
          f4: "Rechnungserstellung",
          f5: "Benutzerdefinierte Workflows",
          f6: "API-Zugriff",
          cta: "Registrieren",
        },
        enterprise: {
          name: "Enterprise",
          priceUnit: "+ 5 $/Nutzer über 25",
          userLimit: "25+ Nutzer",
          f1: "25 Nutzer inklusive",
          f2: "5 $/Monat pro aktivem Nutzer über 25",
          f3: "Alles unbegrenzt",
          f4: "Dedizierter Support",
          f5: "Benutzerdefinierte Integrationen",
          f6: "SLA-Garantie",
          cta: "Jetzt starten",
        },
      },
    },
    compliance: {
      heading: "Sicherheit auf Enterprise-Niveau",
      subheading:
        "Entwickelt mit Compliance und Sicherheit als grundlegenden Anforderungen",
      soc2: {
        name: "An SOC 2 ausgerichtet",
        description: "Aufgebaut nach den SOC 2 Trust Services Criteria",
      },
      hipaa: {
        name: "HIPAA-orientierte Kontrollen",
        description:
          "Schutzmaßnahmen nach Standards für geschützte Gesundheitsinformationen",
      },
      gdpr: {
        name: "DSGVO-bereit",
        description: "Integrierte Kontrollen für Datenschutz und Datensicherheit",
      },
      encryption: {
        title: "Verschlüsselung im Ruhezustand und bei der Übertragung",
        description: "AES-256-GCM-Verschlüsselung für alle Daten",
      },
      auth: {
        title: "Sichere Authentifizierung",
        description:
          "bcrypt-Hashing mit JWT-Tokens und Rotation der Refresh-Tokens",
      },
      audit: {
        title: "Audit-Protokollierung",
        description: "Vollständiger Audit-Trail für Compliance und Transparenz",
      },
      rbac: {
        title: "Rollenbasierte Zugriffskontrolle",
        description: "Fein abgestufte Berechtigungen für Teams und Daten",
      },
      trustLead: "Auf Vertrauen ausgelegt",
      trustBody:
        "Die Sicherheit Ihrer Daten hat für uns höchste Priorität. Wir richten uns nach anerkannten Sicherheitsstandards und überprüfen unsere Kontrollen regelmäßig.",
    },
    cta: {
      headline: "Bereit, Ihr Projektmanagement zu transformieren?",
      subhead: "Schließen Sie sich Tausenden von Teams an, die wxKanban nutzen, um ihre Workflows zu optimieren",
      getStarted: "Loslegen",
      viewPricing: "Preise ansehen",
    },
    footer: {
      tagline: "KI-gestütztes Projektmanagement für moderne Teams",
      product: "Produkt",
      company: "Unternehmen",
      legal: "Rechtliches",
      features: "Funktionen",
      pricing: "Preise",
      documentation: "Dokumentation",
      about: "Über uns",
      blog: "Blog",
      contact: "Kontakt",
      bio: "Biografie",
      privacy: "Datenschutz",
      terms: "AGB",
      security: "Sicherheit",
      rights: "© 2026 wxKanban. Alle Rechte vorbehalten.",
    },
  },
  login: {
    title: "Willkommen zurück",
    subtitle: "Melden Sie sich bei Ihrem wxKanban-Konto an",
    email: "E-Mail",
    emailPlaceholder: "sie@unternehmen.de",
    password: "Passwort",
    rememberMe: "Angemeldet bleiben",
    forgotPassword: "Passwort vergessen?",
    signIn: "Anmelden",
    signingIn: "Wird angemeldet…",
    genericError: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
    noAccount: "Sie haben noch kein Konto?",
    register: "Registrieren Sie Ihr Unternehmen",
    tagline: "Plattformübergreifende Aufgabenverwaltung",
    emailLabel: "E-Mail-Adresse",
    showPasswordAria: "Zum Anzeigen des Passworts gedrückt halten",
    registerLink: "Hier registrieren",
    connectPmPrompt: "Oder verbinden Sie ein PM-System, um loszulegen:",
    loginFailed: "Anmeldung fehlgeschlagen",
  },
  forgotPassword: {
    title: "Passwort vergessen",
    description: "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.",
    successMessage: "Wenn ein Konto mit dieser E-Mail-Adresse existiert, wurde ein Link zum Zurücksetzen des Passworts gesendet. Überprüfen Sie Ihren Posteingang.",
    backToSignIn: "Zurück zur Anmeldung",
    emailLabel: "E-Mail-Adresse",
    emailPlaceholder: "sie@beispiel.de",
    submit: "Link zum Zurücksetzen senden",
    submitting: "Wird gesendet...",
    errorFailed: "Link zum Zurücksetzen konnte nicht gesendet werden",
  },
  resetPassword: {
    title: "Passwort zurücksetzen",
    subtitle: "Geben Sie unten Ihr neues Passwort ein.",
    newPassword: "Neues Passwort",
    confirmNewPassword: "Neues Passwort bestätigen",
    updatePassword: "Passwort aktualisieren",
    updating: "Wird aktualisiert...",
    successMessage: "Ihr Passwort wurde erfolgreich zurückgesetzt. Weiterleitung zur Anmeldung...",
    backToSignIn: "Zurück zur Anmeldung",
    errMissingLink: "Dieser Link zum Zurücksetzen des Passworts ist ungültig oder unvollständig.",
    errMissingToken: "Das Zurücksetzungs-Token fehlt. Bitte verwenden Sie den Link aus Ihrer E-Mail.",
    errPasswordMin: "Das Passwort muss mindestens 8 Zeichen lang sein",
    errPasswordMismatch: "Die Passwörter stimmen nicht überein",
    errFailed: "Passwort konnte nicht zurückgesetzt werden",
  },
  changePassword: {
    title: "Passwort ändern",
    welcome: "Willkommen, {{name}}! Bitte ändern Sie aus Sicherheitsgründen Ihr temporäres Passwort, bevor Sie fortfahren.",
    currentLabel: "Aktuelles Passwort (temporär)",
    currentPlaceholder: "Geben Sie Ihr temporäres Passwort ein",
    newLabel: "Neues Passwort",
    newPlaceholder: "Erstellen Sie ein neues Passwort (mind. 8 Zeichen)",
    confirmLabel: "Neues Passwort bestätigen",
    confirmPlaceholder: "Bestätigen Sie Ihr neues Passwort",
    submit: "Passwort ändern und fortfahren",
    needHelp: "Brauchen Sie Hilfe? Wenden Sie sich an Ihren Administrator oder das",
    supportTeam: "Support-Team",
    errMinLength: "Das neue Passwort muss mindestens 8 Zeichen lang sein",
    errMismatch: "Die Passwörter stimmen nicht überein",
    errSameAsOld: "Das neue Passwort muss sich vom aktuellen Passwort unterscheiden",
    errFailed: "Passwort konnte nicht geändert werden",
    successRedirect: "Passwort erfolgreich geändert! Weiterleitung zum Dashboard...",
  },
  appnav: {
    dashboard: "Dashboard",
    teamMembers: "Teammitglieder",
    projects: "Projekte",
    pmIntegrations: "PM-Integrationen",
    myTasks: "Meine Aufgaben",
    workHub: "Arbeitsbereich",
    timeBilling: "Zeit & Abrechnung",
    invoices: "Rechnungen",
    reports: "Berichte",
    settings: "Einstellungen",
    gettingStarted: "Erste Schritte",
    manual: "Handbuch", // [SCOPE 141 / T016]
    help: "Hilfe",
    signOut: "Abmelden",
  },
  register: {
    next: "Weiter",
    loading: "Wird geladen...",
    back: "Zurück",
    companyTitle: "Unternehmensinformationen",
    companySubtitle: "Erzählen Sie uns von Ihrem Unternehmen, damit wir Ihre Abrechnung und Kommunikation einrichten können",
    companyNameLabel: "Firmenname *",
    billingEmailLabel: "Rechnungs-E-Mail *",
    billingAddressLabel: "Rechnungsadresse *",
    countryLabel: "Land *",
    phoneLabel: "Telefonnummer *",
    referredByLabel: "Empfohlen von (optional)",
    referredByHint: "Wenn ein Vertriebspartner Sie empfohlen hat, geben Sie dessen Code ein.",
    errCompanyNameRequired: "Firmenname ist erforderlich",
    errCompanyNameMin: "Der Firmenname muss mindestens 2 Zeichen lang sein",
    errCompanyNameMax: "Der Firmenname muss weniger als 255 Zeichen lang sein",
    errBillingEmailRequired: "Rechnungs-E-Mail ist erforderlich",
    errEmailInvalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    errBillingAddressRequired: "Rechnungsadresse ist erforderlich",
    errBillingAddressMin: "Die Rechnungsadresse muss mindestens 10 Zeichen lang sein",
    errBillingAddressMax: "Die Rechnungsadresse muss weniger als 500 Zeichen lang sein",
    errCountryRequired: "Land ist erforderlich",
    errCountryInvalid: "Bitte wählen Sie einen gültigen Ländercode",
    errPhoneRequired: "Telefonnummer ist erforderlich",
    errPhoneMin: "Die Telefonnummer muss mindestens 10 Zeichen lang sein",
    errPhoneMax: "Die Telefonnummer muss weniger als 20 Zeichen lang sein",
    adminTitle: "Administratorkonto erstellen",
    adminSubtitle: "Richten Sie Ihr Administratorkonto ein, um auf wxKanban zuzugreifen. Dies ist der erste Benutzer, der für Ihr Unternehmen erstellt wird.",
    emailLabel: "E-Mail-Adresse *",
    firstNameLabel: "Vorname *",
    lastNameLabel: "Nachname (optional)",
    passwordLabel: "Passwort *",
    pwReqTitle: "Passwortanforderungen:",
    pwReqLength: "Mindestens 12 Zeichen",
    pwReqUpper: "Mindestens ein Großbuchstabe",
    pwReqLower: "Mindestens ein Kleinbuchstabe",
    pwReqNumber: "Mindestens eine Zahl",
    pwReqSpecial: "Mindestens ein Sonderzeichen (!@#$%^&*)",
    termsPrefix: "Mit der Erstellung eines Kontos stimmen Sie unseren",
    termsOfService: "Nutzungsbedingungen",
    and: "und",
    privacyPolicy: "Datenschutzrichtlinie",
    creatingAccount: "Konto wird erstellt...",
    completeRegistration: "Registrierung abschließen",
    errEmailRequired: "E-Mail ist erforderlich",
    errPasswordRequired: "Passwort ist erforderlich",
    errPasswordRequirements: "Das Passwort erfüllt nicht alle Anforderungen",
    errFirstNameRequired: "Vorname ist erforderlich",
    errFirstNameLength: "Der Vorname muss zwischen 1 und 100 Zeichen lang sein",
    errLastNameLength: "Der Nachname muss zwischen 1 und 100 Zeichen lang sein",
    paymentMethod: "Zahlungsmethode",
    preparingPayment: "Sichere Zahlung wird vorbereitet…",
    initPaymentError: "Zahlung konnte nicht initialisiert werden",
    subscribingTo: "Sie abonnieren",
    at: "für",
    perMonth: "/Monat",
    stripeNotice: "Kartendaten werden direkt von Stripe verarbeitet. Keine sensiblen Daten erreichen unsere Server.",
    paymentFailedCard: "Zahlung fehlgeschlagen. Bitte überprüfen Sie Ihre Karte und versuchen Sie es erneut.",
    paymentFailed: "Zahlung fehlgeschlagen",
    processing: "Wird verarbeitet…",
    completePayment: "Zahlung abschließen",
    startPromo: "Kostenlosen Zeitraum starten",
    inviteChecking: "Ihre Einladung wird geprüft…",
    inviteInvalid: "Dieser Einladungslink ist ungültig. Er wurde möglicherweise bereits verwendet, widerrufen oder ist abgelaufen.",
    inviteFreeMonths: "Sie wurden eingeladen: Ihre ersten {{days}} Tage mit {{plan}} sind kostenlos.",
    inviteBoundTo: "Diese Einladung gilt für {{email}} — Sie registrieren sich mit dieser Adresse.",
    promoFreeForDays: "Die ersten {{days}} Tage sind kostenlos.",
    promoFirstChargeOn: "Ihre erste Zahlung von {{amount}} erfolgt am {{date}}.",
    promoFirstChargeAfter: "Ihre erste Zahlung von {{amount}} erfolgt nach {{days}} Tagen.",
    promoCardNotChargedToday: "Ihre Karte wird heute nicht belastet. Wir hinterlegen sie, damit Ihr Abonnement automatisch weiterläuft.",
    tierSingleUser: "Einzelbenutzer",
    tierSmallBusiness: "Kleines/Mittleres Unternehmen",
    tierConsultant: "Berater",
    tierEnterprise: "Enterprise",
    tierFree: "Kostenlos",
    confTitle: "Bestätigen Sie Ihre Registrierung",
    confSubtitle: "Bitte überprüfen Sie Ihre Angaben, bevor Sie die Registrierung abschließen",
    confCompanyDesc: "Details zu Ihrer Organisation",
    confCompanyName: "Firmenname",
    confBillingEmail: "Rechnungs-E-Mail",
    subscriptionPlan: "Abonnementplan",
    subscriptionPlanDesc: "Ihre ausgewählte Stufe und Preisgestaltung",
    selectedPlan: "Ausgewählter Plan",
    monthlyPrice: "Monatlicher Preis",
    adminAccount: "Administratorkonto",
    adminAccountDesc: "Ihre Administrator-Anmeldedaten",
    nameLabel: "Name",
    emailShort: "E-Mail",
    termsClickPrefix: "Indem Sie auf „Registrierung abschließen“ klicken, stimmen Sie unseren",
    title: "Willkommen bei wxKanban",
    settingUp: "Einrichten Ihres",
    account: "Kontos",
    stepCompany: "Unternehmen",
    stepPayment: "Zahlung",
    stepAdmin: "Admin",
    successTitle: "Unternehmen erfolgreich erstellt!",
    verificationSent: "Eine Bestätigungs-E-Mail wurde gesendet an",
    whatsNext: "Nächste Schritte:",
    next1: "Prüfen Sie Ihre E-Mail und klicken Sie auf den Bestätigungslink",
    next2: "Melden Sie sich bei Ihrem neuen wxKanban-Konto an",
    next3: "Richten Sie Ihr Team ein und beginnen Sie mit der Projektverwaltung",
    goToLogin: "Zur Anmeldung",
    createAnother: "Weiteres Konto erstellen",
    haveAccount: "Haben Sie bereits ein Konto?",
    signIn: "Anmelden",
  },
  settingsPage: {
    title: "Einstellungen",
    subtitle: "Verwalten Sie Ihre Unternehmens- und Kontoeinstellungen",
    tabCompany: "Unternehmen",
    tabProfile: "Profil",
    tabNotifications: "Benachrichtigungen",
    tabSecurity: "Sicherheit",
    companyInfo: "Unternehmensinformationen",
    companyInfoDesc: "Aktualisieren Sie Ihre Unternehmensdaten und Rechnungsinformationen",
    profileInfo: "Profilinformationen",
    profileInfoDesc: "Aktualisieren Sie Ihre persönlichen Daten und Einstellungen",
    language: "Sprache",
    changePassword: "Passwort ändern",
    notificationPrefs: "Benachrichtigungseinstellungen",
    securitySettings: "Sicherheitseinstellungen",
    saveChanges: "Änderungen speichern",
    saveProfile: "Profil speichern",
  },
  dashboard: {
    failedToFetch: "Abruf fehlgeschlagen: {{status}}",
    failedToLoadPendingTasks: "Ausstehende Aufgaben konnten nicht geladen werden",
    adminTitle: "Admin-Dashboard",
    lastUpdated: "Zuletzt aktualisiert: {{time}}",
    welcome: "Willkommen, {{email}}",
    panelCompany: "Unternehmen",
    totalUsers: "Benutzer gesamt",
    activeUsers: "Aktive Benutzer",
    byRole: "Nach Rolle:",
    panelProjects: "Projekte",
    totalProjects: "Projekte gesamt",
    active: "Aktiv",
    panelTeamSummary: "Team-Übersicht",
    total: "Gesamt",
    panelFinancials: "Finanzen",
    revenue: "Umsatz (diesen Monat)",
    invoices: "Rechnungen",
    outstanding: "Ausstehend",
    panelActivity: "Aktivitäten",
    loadingActivities: "Aktivitäten werden geladen…",
    noActivity: "Keine aktuellen Aktivitäten",
    panelPendingTasks: "Ausstehende Aufgaben",
    loading: "Wird geladen…",
    noActiveProjects: "Keine aktiven Projekte.",
    colProject: "Projekt",
    colOpen: "Offen",
    colInProgress: "In Arb.",
  },
  projects: {
    title: "Projekte",
    noProjects: "Noch keine Projekte",
    count: "{{count}} Projekte",
    countOne: "1 Projekt",
    showAll: "Alle anzeigen",
    myProjects: "Meine Projekte",
    newProject: "Neues Projekt",
    searchPlaceholder: "Projekte suchen…",
    sort: "Sortieren:",
    sortUpdated: "Aktualisiert",
    sortCreated: "Erstellt",
    sortName: "Name",
    loadError: "Projekte konnten nicht geladen werden",
    noMatch: "Keine Projekte entsprechen den aktuellen Filtern",
    clearFilters: "Filter zurücksetzen",
    createFirst: "Erstellen Sie Ihr erstes Projekt",
  },
  tasks: {
    back: "Zurück zum Dashboard",
    title: "Meine Aufgaben",
    lastSync: "Letzte Synchronisierung:",
    syncNow: "Jetzt synchronisieren",
    syncing: "Wird synchronisiert…",
    allSources: "Alle Quellen",
    allPriorities: "Alle Prioritäten",
    priorityCritical: "Kritisch",
    priorityHigh: "Hoch",
    priorityMedium: "Mittel",
    priorityLow: "Niedrig",
    allStatuses: "Alle Status",
    statusOpen: "Offen",
    statusInProgress: "In Bearbeitung",
    statusCompleted: "Abgeschlossen",
    statusOrphaned: "Verwaist",
    sortUpdated: "Kürzlich aktualisiert",
    sortDue: "Fälligkeitsdatum",
    sortPriority: "Priorität",
    sortTitle: "Titel",
    loading: "Aufgaben werden geladen…",
    noTasks: "Noch keine Aufgaben",
    connectHint1: "Verbinden Sie ein PM-System über",
    integrations: "Integrationen",
    connectHint2: "und klicken Sie auf Synchronisieren, um Ihre zugewiesenen Aufgaben zu importieren.",
  },
  integrations: {
    title: "Integrationen von Projektmanagement-Systemen",
    subtitle: "Verbinden Sie Ihre Projektmanagement-Tools mit wxKanban",
    viewTasks: "Meine Aufgaben anzeigen →",
    connectedSuccess: "✓ Projektmanagement-System erfolgreich verbunden!",
    loading: "Integrationen werden geladen...",
    connectedSince: "Verbunden seit {{date}}",
    notConnected: "Nicht verbunden",
    active: "✓ Aktiv",
    disconnect: "Trennen",
    connect: "Verbinden",
    needHelp: "Hilfe bei der Verbindung benötigt?",
    helpText: "Jedes Projektmanagement-System erfordert OAuth-Anmeldedaten. Anweisungen zur Einrichtung finden Sie in der Dokumentation.",
    confirmDisconnect: "{{system}} trennen?",
  },
  invoices: {
    title: "Rechnungen",
    subtitle: "Erstellen und verwalten Sie Kundenrechnungen aus abrechenbaren Zeiteinträgen",
    generate: "Rechnung erstellen",
    statTotal: "Rechnungen gesamt",
    statRevenue: "Gesamtumsatz",
    statSent: "Gesendete Rechnungen",
    modalTitle: "Neue Rechnung erstellen",
    modalDesc: "Wählen Sie den Datumsbereich für abrechenbare Zeiteinträge",
    startDate: "Startdatum",
    endDate: "Enddatum",
    generateConfirm: "Erstellen",
    history: "Rechnungsverlauf",
    emptyTitle: "Noch keine Rechnungen erstellt",
    emptyHint: "Erstellen Sie Ihre erste Rechnung, um zu beginnen",
    colNumber: "Rechnungsnr.",
    colDate: "Datum",
    colBilledTo: "Rechnung an",
    colHours: "Stunden",
    colAmount: "Betrag",
    colStatus: "Status",
    colActions: "Aktionen",
    statusSent: "Gesendet",
    statusDraft: "Entwurf",
    na: "k. A.",
    detailTitle: "Rechnung {{number}}",
    generatedOn: "Erstellt am {{date}}",
    hoursWorked: "Geleistete Stunden",
    totalAmount: "Gesamtbetrag",
    downloadPdf: "PDF herunterladen",
    sendInvoice: "Rechnung senden",
    resend: "Erneut senden",
  },
  reports: {
    title: "Berichte",
    subtitle: "Detaillierte Analysen zu Zeiterfassung, Projekten und Teamleistung anzeigen",
    tabTime: "Zeitberichte",
    tabProjects: "Projektberichte",
    dateRange: "Datumsbereich",
    exportCsv: "CSV exportieren",
    timeByMember: "Zeit nach Teammitglied",
    timeByMemberDesc: "Aufschlüsselung der erfassten Stunden je Teammitglied",
    selectDateRange: "Wählen Sie einen Datumsbereich, um Zeitberichte anzuzeigen",
    colMember: "Teammitglied",
    colTotalHours: "Gesamtstunden",
    colBillableHours: "Abrechenbare Stunden",
    colBillablePct: "Abrechenbar %",
    projectPerformance: "Projektleistung",
    projectPerformanceDesc: "Überblick über alle Projekte und deren Fortschritt",
    noProjects: "Keine Projekte gefunden",
    colProject: "Projekt",
    colHours: "Stunden",
    colTasks: "Aufgaben",
    colCompletion: "Fertigstellung",
    colTeamSize: "Teamgröße",
  },
  workhub: {
    summaryTotal: "Aufgaben gesamt",
    summaryPastDue: "Überfällig",
    summaryAging: "Über 30 Tage",
    changeStatus: "Aufgabenstatus ändern",
    task: "Aufgabe",
    newStatus: "Neuer Status",
    blockedDesc: "eine externe Abhängigkeit verhindert den Fortschritt.",
    suspendedDesc: "zur Neubewertung pausiert.",
    reason: "Grund",
    reasonPlaceholder: "Erklären Sie, warum diese Aufgabe pausiert wird...",
    reasonRequired: "Für blockierte oder ausgesetzte Aufgaben ist ein Grund erforderlich.",
    updateStateError: "Aufgabenstatus konnte nicht aktualisiert werden",
    unknownError: "Unbekannter Fehler",
    submit: "Absenden",
    saving: "Wird gespeichert...",
    cancel: "Abbrechen",
    saveTimeEntry: "Zeiteintrag speichern",
    duration: "Dauer",
    billableQ: "Abrechenbar?",
    billable: "Abrechenbar",
    nonBillable: "Nicht abrechenbar",
    notesOptional: "Notizen (optional)",
    notesPlaceholder: "Woran haben Sie gearbeitet?",
    saveEntry: "Eintrag speichern",
    saveEntryError: "Eintrag konnte nicht gespeichert werden",
    taskActivity: "Aufgabenaktivität",
    loadingActivity: "Aufgabenaktivität wird geladen...",
    loadDetailError: "Aufgabendetails konnten nicht geladen werden",
    timeEntries: "Zeiteinträge ({{count}})",
    noTimeEntries: "Noch keine Zeiteinträge.",
    invoiced: "Abgerechnet",
    statusHistory: "Statusverlauf ({{count}})",
    noStateChanges: "Keine Statusänderungen erfasst.",
    pmSyncState: "PM-Synchronisierungsstatus",
    syncStatus: "Status:",
    syncUnknown: "unbekannt",
    lastSyncLabel: "Letzte Synchronisierung: {{time}}",
    retries: "Wiederholungen: {{count}}",
    errorLabel: "Fehler: {{message}}",
    resolved: "Gelöst: {{time}}",
    aiBadge: "KI",
    manualBadge: "Manuell",
    running: "läuft",
    title: "Berater-Arbeitszentrum",
    subtitle: "Einheitliche Aufgabenliste mit Projektfilterung und Zeiterfassung per Klick.",
    allProjects: "Alle Projekte",
    timerRunning: "Timer läuft",
    stopTimer: "Timer stoppen",
    inactivityTitle: "Der Timer läuft seit über 8 Stunden",
    inactivityBody: "Ist dies noch aktive Arbeit? Stoppen Sie den Timer, wenn Sie weg waren.",
    keepRunning: "Weiterlaufen lassen",
    loadingQueue: "Berater-Warteschlange wird geladen...",
    loadError: "Daten des Arbeitszentrums konnten nicht geladen werden.",
    assignedQueue: "Zugewiesene Arbeitsliste",
    noTasks: "Keine zugewiesenen Aufgaben für diesen Filter gefunden.",
    compliance: "Compliance",
    activity: "Aktivität",
    blocked: "Blockiert",
    suspended: "Ausgesetzt",
    stop: "Stopp",
    startTimer: "Timer starten",
    activeTask: "Aktive Aufgabe",
    billablePreview: "Abrechnungsfertige Vorschau",
    complianceFlaggedTasks: "Für Compliance markierte Aufgaben: {{count}}",
    loadingPreview: "Abrechenbare Vorschau wird geladen...",
    previewError: "Abrechenbare Vorschau konnte nicht geladen werden.",
    previewSummary: "Einträge: {{count}} • Abrechenbare Stunden gesamt: {{hours}}",
    sourceBreakdown: "Aufschlüsselung nach Quelle:",
    manual: "Manuell:",
    ai: "KI:",
    complianceReviewPanel: "Compliance-Prüfungsbereich",
    loadingFlags: "Compliance-Markierungen werden geladen...",
    flagsError: "Compliance-Markierungen konnten nicht geladen werden.",
    noFlags: "Keine offenen Compliance-Markierungen.",
  },
  billing: {
    title: "Abrechnungskonfiguration",
    subtitle: "Konfigurieren Sie Stundensätze für Zeiterfassung und Rechnungsstellung. Sätze folgen einer Hierarchie: Benutzer → Projekt → Unternehmensstandard.",
    fetchError: "Abrechnungssätze konnten nicht abgerufen werden",
    updateError: "Satz konnte nicht aktualisiert werden",
    updateSuccess: "Abrechnungssatz erfolgreich aktualisiert",
    invalidRate: "Bitte geben Sie einen gültigen Satz ein",
    resolutionTitle: "Reihenfolge der Satzauflösung:",
    resolutionBody: "Bei der Berechnung abrechenbarer Beträge prüft das System die Sätze in dieser Reihenfolge: 1) benutzerspezifischer Satz, 2) projektspezifischer Satz, 3) Unternehmensstandardsatz.",
    companyRate: "Unternehmensstandardsatz",
    companyRateDesc: "Ausweichsatz, wenn kein Benutzer- oder Projektsatz festgelegt ist",
    hourlyRate: "Stundensatz (USD)",
    projectRates: "Projektspezifische Sätze",
    projectRatesDesc: "Unternehmensstandard für bestimmte Projekte überschreiben",
    projectIdLabel: "Projekt-ID: {{id}}",
    userRates: "Benutzerspezifische Sätze",
    userRatesDesc: "Alle anderen Sätze für bestimmte Teammitglieder überschreiben",
    noTeamMembers: "Keine Teammitglieder gefunden",
    customRate: "Individueller Satz",
    usingDefault: "Standard wird verwendet",
    defaultPlaceholder: "Standard",
  },
  verification: {
    noToken: "Kein Bestätigungstoken angegeben. Bitte überprüfen Sie den Link in Ihrer E-Mail.",
    verifiedSuccess: "Ihre E-Mail-Adresse wurde erfolgreich bestätigt!",
    verifyFailed: "E-Mail-Bestätigung fehlgeschlagen. Der Link ist möglicherweise abgelaufen.",
    unexpectedError: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.",
    resendFailed: "Erneutes Senden der Bestätigungs-E-Mail fehlgeschlagen. Bitte versuchen Sie es erneut.",
    networkError: "Netzwerkfehler. Bitte versuchen Sie es erneut.",
    titleLoading: "E-Mail wird bestätigt...",
    titleSuccess: "E-Mail bestätigt!",
    titleError: "Bestätigung fehlgeschlagen",
    descLoading: "Bitte warten Sie, während wir Ihre E-Mail-Adresse bestätigen",
    descSuccess: "Sie können sich jetzt bei Ihrem Konto anmelden",
    descError: "Bei Ihrer Bestätigung ist ein Problem aufgetreten",
    signInNow: "Jetzt anmelden",
    resending: "Wird erneut gesendet...",
    resendButton: "Bestätigungs-E-Mail erneut senden",
    resentNotice: "Bestätigungs-E-Mail erneut gesendet! Überprüfen Sie Ihren Posteingang.",
    backToSignIn: "Zurück zur Anmeldung",
    redirecting: "Weiterleitung zur Anmeldung in 3 Sekunden...",
  },
  invitePage: {
    errorNoToken: "Kein Einladungstoken angegeben",
    errorInvalidExpired: "Ungültige oder abgelaufene Einladung",
    errorPasswordMismatch: "Die Passwörter stimmen nicht überein",
    errorPasswordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein",
    errorAcceptFailed: "Einladung konnte nicht angenommen werden",
    errorUnexpected: "Ein unerwarteter Fehler ist aufgetreten",
    invalidTitle: "Ungültige Einladung",
    invalidDescription: "Dieser Einladungslink ist ungültig oder abgelaufen.",
    invalidContactPrompt: "Bitte wenden Sie sich an die Person, die Sie eingeladen hat, um eine neue Einladung anzufordern.",
    goToSignIn: "Zur Anmeldung",
    acceptedTitle: "Willkommen im Team!",
    acceptedDescription: "Ihr Konto wurde erfolgreich erstellt.",
    acceptedBody: "Sie können sich jetzt anmelden, um auf den wxKanban-Arbeitsbereich Ihres Unternehmens zuzugreifen.",
    signInNow: "Jetzt anmelden",
    joinTitle: "{{companyName}} beitreten",
    invitedBy: "Sie wurden von {{inviterName}} eingeladen, als {{role}} beizutreten",
    emailLabel: "E-Mail",
    firstNameLabel: "Vorname",
    lastNameLabel: "Nachname",
    passwordLabel: "Passwort",
    confirmPasswordLabel: "Passwort bestätigen",
    creatingAccount: "Konto wird erstellt...",
    acceptAndCreate: "Einladung annehmen & Konto erstellen",
    alreadyHaveAccount: "Sie haben bereits ein Konto?",
    signInHere: "Hier anmelden",
  },
  inviteAccept: {
    subtitle: "Team-Einladung",
    validating: "Einladung wird überprüft...",
    expiredTitle: "Einladung abgelaufen",
    expiredBody: "Dieser Einladungslink ist abgelaufen. Bitten Sie Ihren Administrator, ihn erneut zu senden.",
    usedTitle: "Einladung bereits verwendet",
    invalidTitle: "Ungültige Einladung",
    usedBody: "Diese Einladung wurde bereits angenommen. Versuchen Sie sich anzumelden.",
    invalidBody: "Dieser Einladungslink ist ungültig oder abgelaufen.",
    goToLogin: "Zur Anmeldung",
    successTitle: "Konto erstellt!",
    successBody: "Ihr Konto wurde eingerichtet. Sie können sich jetzt bei wxKanban anmelden.",
    logIn: "Anmelden",
    joinCompany: "{{company}} beitreten",
    creatingAccountFor: "Konto wird erstellt für",
    fullNameLabel: "Vollständiger Name",
    passwordLabel: "Passwort",
    passwordPlaceholder: "Mind. 8 Zeichen",
    confirmPasswordLabel: "Passwort bestätigen",
    confirmPasswordPlaceholder: "Passwort erneut eingeben",
    timezoneLabel: "Zeitzone",
    timezoneHint: "Automatisch aus Ihrem Browser erkannt",
    creatingAccount: "Konto wird erstellt...",
    createAccount: "Konto erstellen",
    alreadyHaveAccount: "Sie haben bereits ein Konto?",
    signIn: "Anmelden",
    errorPasswordMismatch: "Passwörter stimmen nicht überein",
    errorPasswordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein",
    errorCreateAccountFailed: "Konto konnte nicht erstellt werden",
    errorUnknown: "Unbekannter Fehler",
  },
  inviteTeam: {
    title: "Teammitglieder einladen",
    subtitle: "Fügen Sie Kolleginnen und Kollegen hinzu, um an Ihren Projekten zusammenzuarbeiten",
    sendInvitations: "Einladungen senden",
    sendInvitationsDesc: "Wählen Sie, wie Sie Ihre Teammitglieder einladen möchten",
    singleInvite: "Einzelne Einladung",
    bulkUpload: "Massen-Upload",
    sendMore: "Weitere Einladungen senden",
    viewTeam: "Team anzeigen",
    tipsTitle: "Tipps zum Einladen von Teammitgliedern",
    tipEmails: "Achten Sie darauf, die korrekten E-Mail-Adressen zu verwenden, um Zustellfehler zu vermeiden",
    tipRoles: "Wählen Sie passende Rollen: Admin (Vollzugriff), Manager (kann Projekte verwalten) oder Mitglied (kann Aufgaben ansehen und erledigen)",
    tipCsv: "Verwenden Sie für Massen-Uploads eine CSV-Datei mit den Spalten: email, usertype, firstName, lastName",
    tipExpiry: "Einladungen verfallen nach 7 Tagen, wenn sie nicht angenommen werden",
  },
  teamDashboard: {
    title: "Mein Dashboard",
    welcomeBack: "Willkommen zurück, {{email}}",
    assignedProjects: "Zugewiesene Projekte",
    openTasks: "Offene Aufgaben",
    hoursThisWeek: "Stunden diese Woche",
    noProjectsAssigned: "Ihnen sind noch keine Projekte zugewiesen.",
    workHubTitle: "Consultant Work Hub",
    workHubDescription: "Öffnen Sie Ihre einheitliche Aufgabenliste und erfassen Sie die Zeit direkt für zugewiesene Arbeiten.",
    openWorkHub: "Work Hub öffnen",
  },
  distributorSignup: {
    errorPasswordsMismatch: "Passwörter stimmen nicht überein",
    errorPasswordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein",
    errorAcceptTerms: "Bitte akzeptieren Sie die Vertriebspartner-Bedingungen",
    errorRegistrationFailed: "Registrierung fehlgeschlagen",
    errorUnknown: "Unbekannter Fehler",
    successHeading: "Alles bereit",
    successSignedInPrefix: "Ihr kostenloses Vertriebspartner-Konto ist bereit. Sie sind angemeldet als",
    referralCodeLabel: "Ihr Empfehlungscode",
    copyCodeTitle: "Code kopieren",
    referralCodeHelp: "Teilen Sie diesen Code mit den von Ihnen empfohlenen Personen. Wenn sie ihn bei der Registrierung eingeben, wird ihr Konto Ihnen gutgeschrieben.",
    goToDashboard: "Zu Ihrem Dashboard",
    introBannerBefore: "Werden Sie wxKanban-Vertriebspartner. Richten Sie Ihr",
    introBannerFree: "kostenloses",
    introBannerAfter: "Konto unten ein — Sie erhalten einen Empfehlungscode zum Teilen und werden anschließend automatisch angemeldet.",
    fieldBusinessName: "Firmenname *",
    fieldFullName: "Ihr vollständiger Name *",
    fieldEmail: "E-Mail *",
    fieldPassword: "Passwort *",
    placeholderPassword: "Mindestens 8 Zeichen",
    fieldConfirmPassword: "Passwort bestätigen *",
    placeholderConfirmPassword: "Passwort erneut eingeben",
    acceptTermsLabel: "Ich akzeptiere die Vertriebspartner-Bedingungen. Mein Konto ist ein kostenloses Einzelbenutzer-Konto — ich habe kein kostenpflichtiges Abonnement.",
    creatingAccount: "Konto wird erstellt…",
    createAccountButton: "Kostenloses Konto erstellen",
    pageSubtitle: "Vertriebspartner-Anmeldung",
  },
  overviewPage: {
    loadFailed: "Übersichtsdokument konnte nicht geladen werden ({{status}})",
    unknownError: "Unbekannter Fehler",
    dashboard: "Dashboard",
    projectOverview: "Projektübersicht",
    refresh: "Aktualisieren",
    loadErrorTitle: "Übersichtsdokument konnte nicht geladen werden",
  },
  projectWorkspace: {
    // [SCOPE 137 / T019] Change Impact mode (owner-set)
    impactModeTitle: "Change-Impact-Modus",
    impactModeHelp: "Wie die Change-Impact-Prüfung des Kits KI-Änderungen in diesem Projekt behandelt. Gilt für das Kit jedes Entwicklers und hat Vorrang vor einem lokal gesetzten Wert.",
    impactModeNotify: "Benachrichtigen: dem Entwickler zeigen, was eine Änderung betrifft, nie fragen",
    impactModeBoundaries: "Grenzen: nur fragen, wenn eine Änderung einen anderen Scope oder einen abgenommenen Test erreicht (Standard)",
    impactModeAlways: "Immer: fragen, sobald eine Änderung etwas anderes betrifft",
    impactModeNotSet: "Hier nicht festgelegt. Das Kit jedes Entwicklers verwendet seinen lokalen Wert oder „Grenzen\".",
    impactModeSetBy: "Festgelegt von {{who}} am {{when}}",
    impactModeOwnerOnly: "Nur der Projektinhaber oder ein Firmenadministrator kann dies ändern.",
    impactModeDataLoss: "Möglicher Datenverlust wird in jedem Modus gestoppt.",
    impactModeError: "Die Änderung wurde nicht gespeichert. Bitte versuchen Sie es erneut.",
    complianceTabSettings: "Einstellungen",
    complianceTabDocuments: "Dokumente",
    complianceTabTasks: "Aufgaben",
    complianceTabAudit: "Audit",
    complianceTabReports: "Berichte",
    learningsGenerateFailed: "Fehlgeschlagen ({{status}})",
    learningsPrintHint: "Zum Speichern als PDF die Druckfunktion des Browsers verwenden",
    regenerate: "Neu generieren",
    learningsIframeTitle: "Projekterkenntnisse",
    learningsNoneTitle: "Noch kein Erkenntnisbericht",
    learningsRunPrefix: "Führen Sie",
    learningsRunSuffix: "nach Abschluss der Arbeit aus, um Entscheidungen, Korrekturen und Compliance-Kontrollen zu erfassen.",
    learningsThenGenerate: "Erstellen Sie dann den Bericht unten.",
    generating: "Wird generiert…",
    generateLearningsReport: "Erkenntnisbericht generieren",
    errFailedLoadProject: "Projekt konnte nicht geladen werden",
    confirmArchive: "Dieses Projekt archivieren? Es wird aus der Projektliste ausgeblendet.",
    confirmDelete: "\"{{name}}\" endgültig löschen? Dadurch werden das Projekt und alle zugehörigen Aufgaben, Phasen, Specs und Dokumente entfernt. Dies kann nicht rückgängig gemacht werden.",
    thisProject: "dieses Projekt",
    errFailedDeleteProjectStatus: "Projekt konnte nicht gelöscht werden ({{status}})",
    errFailedDeleteProject: "Projekt konnte nicht gelöscht werden",
    errFailedUpdateProject: "Projekt konnte nicht aktualisiert werden",
    errFailedUpdateAuditType: "Audit-Typ konnte nicht aktualisiert werden",
    loadingProject: "Projekt wird geladen...",
    projectNotFound: "Projekt nicht gefunden",
    backToProjects: "Zurück zu den Projekten",
    projects: "Projekte",
    clientEngagementTitle: "Zugriff über aktive Zusammenarbeit mit {{name}}",
    client: "Kunde",
    clientProjectBadge: "Kundenprojekt — {{name}}",
    editProject: "Projekt bearbeiten",
    auditLabel: "Audit:",
    noAudit: "Kein Audit",
    archiveProject: "Projekt archivieren",
    archive: "Archivieren",
    deleteProjectTitle: "Projekt endgültig löschen",
    delete: "Löschen",
    tabLifecycle: "Lebenszyklus",
    tabDevelopmentPlan: "Entwicklungsplan",
    tabPortfolioAnalyzer: "Portfolio-Analyse",
    tabCompliance: "Compliance",
    tabLearnings: "Erkenntnisse",
    tabKit: "Kit",
    views: "Ansichten",
    viewMatrix: "Matrix",
    viewStack: "Stack",
    viewTheme: "Thema",
    viewBugReports: "Fehlerberichte",
    iframeLifecycle: "Projekt-Lebenszyklus",
    iframeDevplan: "Entwicklungsplan",
    phaseNotStarted: "Diese Phase hat noch nicht begonnen.",
    saveFailed: "Speichern fehlgeschlagen",
    saveErrorIntro: "Beim Speichern ist folgender Fehler aufgetreten:",
    copyToClipboard: "In die Zwischenablage kopieren",
    copy: "Kopieren",
    copyHint: "Klicken Sie auf \"Kopieren\", um diese Fehlermeldung zum Melden in die Zwischenablage zu kopieren.",
    close: "Schließen",
    copyAndClose: "Kopieren & Schließen",
    fieldProjectName: "Projektname",
    fieldDescription: "Beschreibung",
    fieldHourlyRate: "Stundensatz ($)",
    fieldAllocatedHours: "Zugewiesene Stunden",
    fieldPmSystems: "PM-Systeme",
    fieldSecurityClassification: "Sicherheitsklassifizierung",
    fieldAuditType: "Audit-Typ",
    phaseAssignments: "Phasenzuweisungen",
    loadingTeamMembers: "Teammitglieder werden geladen...",
    unassigned: "Nicht zugewiesen",
    reassignPermissionNote: "Nur ADMIN und TEAM_MANAGER können Phasen neu zuweisen.",
    cancel: "Abbrechen",
    saving: "Wird gespeichert...",
    saveChanges: "Änderungen speichern",
  },
  helpPage: {
    backToHelpCenter: "Zurück zum Hilfe-Center",
    examples: "Beispiele",
    wasArticleHelpful: "War dieser Artikel hilfreich?",
    yesItHelped: "Ja, es hat geholfen",
    noNotHelpful: "Nein, nicht hilfreich",
    helpCenter: "Hilfe-Center",
    // [SCOPE 138 / T011] "Learn wxKanban with your AI"
    teachTitle: "wxKanban mit Ihrer KI lernen",
    teachHowTo: "Laden Sie die Lektionen herunter, geben Sie start-here.md (oder das ganze ZIP) an einen beliebigen KI-Chat und sagen Sie „bring es mir bei\". Jede Lektion führt Sie Schritt für Schritt durch eine Aufgabe.",
    teachDownload: "Lektionen herunterladen",
    teachVersion: "wxKanban {{version}} · {{count}} Lektionen",
    teachError: "Der Download ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
    subtitle: "Finden Sie Antworten, Dokumentation und Anleitungen für wxKanban",
    searchPlaceholder: "Nach Hilfeartikeln, CLI-Befehlen oder Anleitungen suchen...",
    searchResults: "Suchergebnisse",
    searching: "Wird gesucht...",
    noResults: "Keine Ergebnisse für \"{{query}}\" gefunden",
    viewsCount: "{{count}} Aufrufe",
    topicsCount: "{{count}} Themen",
    moreCount: "+{{count}} weitere",
  },
  planHelp: {
    title: "Hilfe",
    subtitlePrefix: "Was jeder wxKanban-Bereich leistet und was in Ihrem Tarif enthalten ist. Es wird",
    subtitleSuffix: "standardmäßig angezeigt – wählen Sie einen anderen Tarif zum Vergleich.",
    yourPlan: "Ihr Tarif",
    included: "Enthalten",
    upgradeRequired: "Upgrade erforderlich",
    loading: "Ihr Tarif wird geladen…",
  },
  faqPage: {
    title: "Hilfe & FAQ",
    subtitle: "Durchsuchen Sie die Antworten oder stellen Sie eine Frage, die wir den FAQ hinzufügen.",
    panel: {
      heading: "Häufig gestellte Fragen",
      loading: "Wird geladen…",
      empty: "Noch keine FAQ — stellen Sie unten eine Frage.",
      askPrompt: "Antwort nicht gefunden? Fragen Sie uns",
      placeholder: "Geben Sie Ihre Frage ein…",
      submit: "Frage senden",
      sending: "Senden…",
      tooShort: "Bitte geben Sie eine Frage ein (mindestens 5 Zeichen).",
      submitError: "Ihre Frage konnte nicht gesendet werden.",
      thanks: "Danke — wir beantworten das.",
    },
  },
  aboutPage: {
    title: "Über wxKanban",
    lede: "wxKanban ist ein spezifikationsgetriebener Weg, Software mit KI zu entwickeln – es bringt Ihnen bei, so zu arbeiten wie Profis, und führt eine Anwendung über den gesamten Lebenszyklus von einer chaotischen Idee zu einem sauberen, getesteten, ausgelieferten Ergebnis, auf Fundamenten, die Ihnen wirklich gehören.",
    whatItIsHeading: "Was es ist",
    whatItIsPara1: "wxKanban ist eine spezifikationsgetriebene Entwicklungsumgebung. Sie arbeiten sich durch einen geführten Lebenszyklus mit sechs Phasen – Design → Implementierung → QA → Human Testing → Beta → Release –, in dem nichts ohne eine genehmigte Spezifikation gebaut wird und jede Änderung auf die Spezifikation zurückführbar ist, die sie autorisiert hat. Ebenso wichtig: Es hält Ihre Spezifikationen und Dokumentation konsistent, während sich die Software weiterentwickelt – künftige Änderungen werden mit dem Bereits-Geschriebenen abgeglichen, sodass Ihre Dokumentation mit der Arbeit aktualisiert wird, statt zu veralten.",
    whatItIsIntro: "Rund um diesen Lebenszyklus gibt Ihnen wxKanban, was Sie brauchen, um echte Arbeit im Team zu bewältigen:",
    teamMgmtLabel: "Teamverwaltung",
    teamMgmtBody: "– laden Sie Personen ein, legen Sie ihre Rollen fest und steuern Sie, wer was tun darf.",
    checkoutLabel: "Check-out & Check-in",
    checkoutBody: "– übernehmen Sie einen Scope, um daran zu arbeiten, und geben Sie ihn wieder frei, wenn Sie fertig sind, damit die Arbeit koordiniert bleibt und niemand unbemerkt die Arbeit eines anderen überschreibt.",
    cockpitLabel: "Das Cockpit",
    cockpitBody: "– eine Live-Kommandozentrale für Ihr Projekt: wo es steht, was als Nächstes kommt und jeder wxKanban-Befehl an einem Ort.",
    helpLabel: "Aktive Hilfe im Kontext",
    helpBody: "– Anleitung und Antworten direkt dort, wo Sie arbeiten, sowohl in wxKanban als auch in VS Code, wo das eigentliche Bauen stattfindet.",
    whatItIsClosing: "Das ist der Kern von wxKanban: der spezifikationsgetriebene Lebenszyklus, mit den Werkzeugen, um ihn im Team umzusetzen.",
    whyHeading: "Warum es existiert",
    whyPara1: "KI hat verändert, wie Software gebaut wird, doch der schwierige Teil war nie die Syntax – es war das ",
    whyEmphasis: "Urteilsvermögen",
    whyPara2: ", um eine Anwendung bis zur Auslieferung zu bringen. wxKanban ist dafür gebaut, erfahrenen Entwicklern einen Weg zu geben, dieses Urteilsvermögen ins KI-Zeitalter mitzunehmen, und Neulingen diese Disziplin vom ersten Tag an zu vermitteln – damit KI Ihre Arbeit beschleunigt, statt Ihnen zu entgleiten.",
    groundHeading: "Auf Boden gebaut, der Ihnen gehört",
    groundPara: "Ihre Daten liegen in einer Standard-SQL-Datenbank, Ihre Authentifizierung gehört Ihnen, und Sie können KI-Anbieter jederzeit wechseln. Kein proprietärer Lock-in, kein Anbieter, der zwischen Ihnen und der Software steht, auf die Sie angewiesen sind.",
    behindHeading: "Wer dahintersteht",
    behindPara1: "wxKanban wird gemacht von ",
    behindPara2: ", gegründet von Andy „Cowboy“ Stapleton – der über 40 Jahre lang jeden großen Plattformwechsel mitgemacht hat, vom Mainframe über das Web bis in die Cloud, und anderen Entwicklern hilft, denselben Übergang zu schaffen.",
    contactHeading: "Kontakt aufnehmen",
    contactIntro: "Fragen oder Ideen? Schreiben Sie an",
    contactOutro: "– wir antworten wirklich.",
    phoneLabel: "Telefon:",
  },
  bioPage: {
    title: "Über den Gründer",
    photoAlt: "Porträt des Gründers",
    loading: "Wird geladen…",
  },
  privacyPage: {
    title: "Datenschutzerklärung",
    lastUpdated: "Zuletzt aktualisiert: {{date}}",
    intro:
      "wxKanban („wir“, „wxperts“) respektiert Ihre Privatsphäre. Diese Richtlinie erklärt, was wir erfassen, wenn Sie die wxKanban-Anwendung nutzen, warum, und welche Wahlmöglichkeiten Sie haben. Wir erfassen, was wir zum Betrieb des Dienstes benötigen, wir verkaufen Ihre Daten nicht, und Sie können uns bitten, sie zu löschen.",
    authoritativeNote:
      "Diese Richtlinie wurde zur besseren Verständlichkeit übersetzt. Maßgeblich ist die englische Fassung.",
    sections: {
      s1heading: "Informationen, die wir erfassen",
      s1item1:
        "Konto- und Firmendaten — Ihr Name, Ihre E-Mail-Adresse, Ihr Passwort (nur als sicherer Hash gespeichert), Ihr Firmenname, Ihre Rolle und Ihre Sprachpräferenz.",
      s1item2:
        "Die von Ihnen erstellten Inhalte — Projekte, Aufgaben, Spezifikationen, Notizen, Zeiteinträge, Rechnungen und zugehörige Inhalte, die Sie in die App eingeben.",
      s1item3:
        "Zahlungsinformationen — Abonnement- und Zahlungsdaten werden von unserem Zahlungsdienstleister Stripe verarbeitet. Wir speichern keine vollständigen Kartennummern; wir bewahren Ihren Tarif, Ihren Status und Ihren Zahlungsverlauf auf.",
      s1item4:
        "Verbundene Dienste — wenn Sie ein Projektmanagement-Tool verknüpfen (z. B. Jira, Monday.com, Asana, Trello), speichern wir die zur Synchronisierung erforderlichen Zugriffstokens, verschlüsselt gespeichert.",
      s1item5:
        "Nutzung und Protokolle — grundlegende technische Protokolle (etwa Aktionen, Zeitstempel und Fehler), die dem Betrieb, der Sicherung und der Verbesserung des Dienstes dienen.",
      s2heading: "Wie wir sie verwenden",
      s2item1: "Um die App bereitzustellen und zu betreiben — Ihr Konto, Ihre Projekte, Ihre Zeiterfassung und Ihre Rechnungsstellung.",
      s2item2: "Um Abonnements und Zahlungen über Stripe abzuwickeln.",
      s2item3: "Um mit den von Ihnen gewählten Projektmanagement-Tools zu synchronisieren.",
      s2item4: "Um KI-gestützte Funktionen zu ermöglichen (siehe unten).",
      s2item5: "Um den Dienst zu sichern, Missbrauch zu verhindern und Support zu leisten.",
      s3heading: "KI-Verarbeitung",
      s3body:
        "Einige Funktionen senden von Ihnen bereitgestellte Inhalte (zum Beispiel Spezifikationen oder zugehörigen Text) an Drittanbieter von KI — etwa Google Gemini, OpenAI oder andere, die wir konfigurieren — ausschließlich, um das angeforderte Ergebnis zu erzeugen. Wir verwenden Ihre Inhalte nicht, um unsere eigenen Modelle zu trainieren, und die KI-Anbieter verarbeiten sie unter ihren eigenen Bedingungen als unsere Dienstleister.",
      s4heading: "Mit wem wir sie teilen",
      s4body:
        "Wir verkaufen oder vermieten Ihre Informationen nicht. Wir teilen sie nur mit den Dienstleistern, die uns beim Betrieb von wxKanban unterstützen — zum Beispiel Cloud-Hosting (Amazon Web Services), Zahlungsabwicklung (Stripe) und die oben genannten KI-Anbieter — und nur, damit diese die entsprechenden Dienste für uns erbringen können. Wir können Informationen offenlegen, wenn dies gesetzlich vorgeschrieben ist.",
      s5heading: "Sicherheit",
      s5body:
        "Wir schützen Ihre Daten durch Verschlüsselung bei der Übertragung und im Ruhezustand, gehashte Passwörter und Zugriffskontrollen. Kein System ist vollkommen sicher, doch wir arbeiten fortlaufend daran, Ihre Informationen zu schützen.",
      s6heading: "Wie lange wir sie aufbewahren",
      s6body:
        "Wir bewahren Ihr Konto und die von Ihnen erstellten Inhalte auf, solange Ihr Konto aktiv ist, sowie für einen angemessenen Zeitraum danach, soweit dies für rechtliche, buchhalterische und betriebliche Zwecke erforderlich ist. Sie können uns jederzeit bitten, Ihre Daten zu löschen.",
      s7heading: "Ihre Wahlmöglichkeiten und Rechte",
      s7body:
        "Sie können auf Ihre personenbezogenen Daten zugreifen, sie berichtigen, exportieren oder löschen und Ihre Einwilligung widerrufen, soweit sie gilt. Wenn Sie sich in einer Region mit besonderen Datenschutzrechten befinden (etwa der EU oder dem Vereinigten Königreich gemäß der GDPR oder US-amerikanischen bundesstaatlichen Datenschutzgesetzen), gelten diese Rechte für Sie. Um sie auszuüben, schreiben Sie eine E-Mail an",
      s8heading: "Internationale Übermittlungen",
      s8body:
        "wxKanban wird aus den Vereinigten Staaten betrieben, und Ihre Informationen können dort verarbeitet werden. Durch die Nutzung des Dienstes stimmen Sie dieser Übermittlung zu, vorbehaltlich der in dieser Richtlinie beschriebenen Schutzmaßnahmen.",
      s9heading: "Kontakt",
      s9body: "Fragen zum Datenschutz? Schreiben Sie eine E-Mail an",
    },
  },
  termsPage: {
    title: "Nutzungsbedingungen",
    lastUpdated: "Zuletzt aktualisiert: {{date}}",
    intro:
      "Diese Bedingungen regeln Ihre Nutzung der wxKanban-Anwendung und -Website. Indem Sie ein Konto erstellen oder den Dienst nutzen, stimmen Sie ihnen zu. Bitte lesen Sie auch unsere",
    authoritativeNote:
      "Diese Bedingungen wurden zur besseren Verständlichkeit übersetzt. Maßgeblich ist die englische Fassung.",
    sections: {
      s1heading: "Ihr Konto",
      s1body:
        "Sie sind für die Richtigkeit Ihrer Kontodaten und für die sichere Aufbewahrung Ihrer Anmeldedaten verantwortlich. Sie sind für Aktivitäten verantwortlich, die unter Ihrem Konto stattfinden, sowie für die von Ihnen eingeladenen Teammitglieder.",
      s2heading: "Zulässige Nutzung",
      s2body:
        "Nutzen Sie wxKanban rechtmäßig. Versuchen Sie nicht, den Dienst zu stören oder sich unbefugten Zugriff darauf zu verschaffen, die Daten anderer Nutzer zu missbrauchen oder die Plattform zu nutzen, um rechtswidrige Inhalte zu erstellen oder zu speichern.",
      s3heading: "Abonnements und Abrechnung",
      s3body:
        "Kostenpflichtige Tarife werden über Stripe wiederkehrend abgerechnet, bis sie gekündigt werden. Die Gebühren werden für jeden Abrechnungszeitraum im Voraus berechnet und sind nicht erstattungsfähig, außer wo dies gesetzlich vorgeschrieben ist. Sie können jederzeit kündigen; Ihr Tarif bleibt bis zum Ende des aktuellen Zeitraums aktiv. Über Preisänderungen informieren wir Sie mit angemessener Frist.",
      s4heading: "Ihre Inhalte und Eigentum",
      s4body:
        "Ihnen gehören die Daten und Inhalte, die Sie in wxKanban erstellen. Sie gewähren uns die begrenzte Lizenz, die erforderlich ist, um sie zu hosten, zu verarbeiten, zu sichern und anzuzeigen, damit wir den Dienst erbringen können. Sie können Ihre Inhalte exportieren oder löschen, wie in unserer Datenschutzerklärung beschrieben.",
      s5heading: "KI-generierte Ausgaben",
      s5body:
        "wxKanban nutzt KI, um Ihre Arbeit zu unterstützen. KI-Ausgaben können fehlerhaft sein — Sie sind dafür verantwortlich, alles Generierte zu überprüfen, zu testen und freizugeben, bevor Sie sich darauf verlassen. Wir gewährleisten nicht, dass KI-gestützte Ergebnisse richtig, vollständig oder für einen bestimmten Zweck geeignet sind.",
      s6heading: "Unser geistiges Eigentum",
      s6body:
        "Die wxKanban-Anwendung, ihr Design sowie die Namen wxKanban und wxperts gehören wxperts oder seinen Lizenzgebern. Diese Bedingungen gewähren Ihnen keinerlei Recht an unserer Software oder unserem Branding, das über die bestimmungsgemäße Nutzung des Dienstes hinausgeht.",
      s7heading: "Keine Gewährleistungen",
      s7body:
        "Der Dienst wird „wie besehen“ und „wie verfügbar“ bereitgestellt, ohne jegliche Gewährleistung. Wir bemühen uns, wxKanban zuverlässig und verfügbar zu halten, garantieren jedoch keinen ununterbrochenen oder fehlerfreien Betrieb.",
      s8heading: "Haftungsbeschränkung",
      s8body:
        "Im größtmöglichen gesetzlich zulässigen Umfang haftet wxperts nicht für indirekte, zufällige oder Folgeschäden oder für den Verlust von Daten oder Gewinnen, die sich aus Ihrer Nutzung des Dienstes — oder der Unmöglichkeit, ihn zu nutzen — ergeben.",
      s9heading: "Kündigung",
      s9body:
        "Sie können Ihr Konto jederzeit schließen. Wir können den Zugang aussetzen oder beenden, wenn gegen diese Bedingungen verstoßen wird oder um den Dienst zu schützen. Bei Beendigung gelten die Bestimmungen weiter, die ihrer Natur nach fortbestehen sollten.",
      s10heading: "Anwendbares Recht",
      s10body:
        "wxperts wird aus den Vereinigten Staaten betrieben, und diese Bedingungen unterliegen US-amerikanischem Recht, ohne Rücksicht auf kollisionsrechtliche Grundsätze. Wenn Sie den Dienst aus der Europäischen Union nutzen, hebt nichts in diesen Bedingungen die zwingenden Verbraucher- und Datenschutzrechte auf, die Ihnen nach EU-Recht (einschließlich der GDPR) zustehen; diese Schutzrechte gelten weiterhin für Sie.",
      s11heading: "Änderungen",
      s11body:
        "Wir können diese Bedingungen anpassen, während sich das Produkt weiterentwickelt. Das Datum „Zuletzt aktualisiert“ oben zeigt, wann sie zuletzt geändert wurden; die fortgesetzte Nutzung nach einer Änderung bedeutet, dass Sie sie akzeptieren.",
      s12heading: "Kontakt",
      s12body: "Fragen zu diesen Bedingungen? Schreiben Sie eine E-Mail an",
    },
  },
  adminDashboard: {
    saveFailed: "Speichern fehlgeschlagen",
    editAuditType: "Audit-Typ bearbeiten",
    complianceFramework: "Compliance-Framework",
    none: "— Keiner —",
    title: "Admin-Dashboard",
    welcomeBack: "Willkommen zurück, {{email}}",
    teamMembers: "Teammitglieder",
    activeProjects: "Aktive Projekte",
    hoursThisWeek: "Stunden diese Woche",
    timeBilling: "Zeit & Abrechnung",
    timeBillingDesc: "Erfassen Sie Zeit, legen Sie Abrechnungssätze fest und verwalten Sie abrechenbare Stunden über alle Projekte hinweg.",
    timeBillingPlaceholder: "Zeiteinträge und Abrechnungssätze werden hier angezeigt.",
    systemHealth: "Systemzustand",
    reportsAnalytics: "Berichte & Analysen",
    reportsDesc: "Umsatzübersichten, Stunden pro Projekt, Rentabilität und exportierbare Berichte.",
    reports: "Berichte",
    reportsPlaceholder: "Finanzberichte werden hier angezeigt, sobald Zeiteinträge erfasst wurden.",
  },
  subscriptionCard: {
    statusActive: "Aktiv",
    statusPending: "Ausstehend",
    statusSuspended: "Ausgesetzt",
    currentPlan: "Aktueller Tarif",
    teamSeats: "Team-Plätze",
    seatLimitReached: "Platzlimit erreicht — führen Sie ein Upgrade durch, um weitere Mitglieder einzuladen.",
    upgradePlan: "Tarif upgraden",
  },
  engagementsWidget: {
    title: "Engagements",
    clientFirms: "{{count}} Beratungsunternehmen in Ihren Projekten",
    clientFirmsPlural: "{{count}} Beratungsunternehmen in Ihren Projekten",
    clientsServed: "{{count}} Kunde, den Sie betreuen",
    clientsServedPlural: "{{count}} Kunden, die Sie betreuen",
    viewAll: "Alle anzeigen",
    roleHiring: "Beauftragend",
    roleServing: "Betreuend",
    moreCount: "+ {{count}} weitere",
  },
  activityFeed: {
    title: "Aktivitäten-Feed",
    loading: "Aktuelle Aktivitäten werden geladen...",
    subtitle: "Aktuelle Team- und Projektaktivität",
    viewAll: "Alle anzeigen",
    empty: "Keine aktuelle Aktivität",
    emptyHint: "Aktivitäten erscheinen hier, während Ihr Team arbeitet",
    byUser: "von {{name}}",
    justNow: "gerade eben",
    minutesAgo: "vor {{count}} Min",
    hoursAgo: "vor {{count}} Std",
    daysAgo: "vor {{count}} T",
  },
  dailyDigest: {
    title: "Tägliche Zusammenfassung",
    loadingActivity: "Aktivität von heute wird geladen...",
    subtitle: "Zusammenfassung der heutigen Teamaktivität",
    configure: "Konfigurieren",
    viewAll: "Alle anzeigen",
    noActivity: "Heute noch keine Aktivität",
    activityWillAppear: "Teamaktivität wird hier angezeigt",
    byUser: "von {{name}}",
    moreItems: "+{{count}} weitere Elemente",
    justNow: "gerade eben",
    minutesAgo: "vor {{count}} Min.",
    hoursAgo: "vor {{count}} Std.",
  },
  roleLock: {
    contactAdmin: "Wenden Sie sich an Ihren Administrator, um auf diesen Bereich zuzugreifen.",
    requiresAdmin: "Erfordert Administrator",
  },
  upgradeLock: {
    requiresPlan: "Erfordert den {{plan}}-Tarif",
    upgradePlan: "Tarif upgraden",
  },
  projectProposals: {
    errorFindDraft: "Übersichtsentwurf für diesen Vorschlag konnte nicht gefunden werden",
    errorOpenEditor: "Editor konnte nicht geöffnet werden",
    errorFetch: "Vorschläge konnten nicht abgerufen werden: {{status}}",
    errorLoad: "Vorschläge konnten nicht geladen werden",
    errorUpdateStatus: "Status konnte nicht aktualisiert werden",
    rejectionReasonPrompt: "Ablehnungsgrund (erforderlich):",
    title: "Projektvorschläge",
    addProposal: "Vorschlag hinzufügen",
    loadingProposals: "Vorschläge werden geladen...",
    empty: "Noch keine Vorschläge. Fügen Sie einen hinzu, um zu beginnen.",
    status_pending: "Ausstehend",
    status_good_idea: "Gute Idee",
    status_accepted: "Angenommen",
    status_rejected: "Abgelehnt",
    editTitle: "Übersichts-Editor für diesen Vorschlag öffnen",
    goodIdea: "Gute Idee",
    reject: "Ablehnen",
    accept: "Annehmen",
    createProject: "Projekt erstellen",
    reopen: "Wieder öffnen",
    projectCreated: "Projekt erstellt",
  },
  systemHealth: {
    title: "Systemzustand",
    checked: "Geprüft {{time}}",
    refresh: "Aktualisieren",
    runningDiagnostics: "Diagnose läuft…",
    allSystemsOperational: "Alle Systeme betriebsbereit",
    driftOrMissingDetected: "Schema-Abweichung oder fehlende Tabellen erkannt",
    databaseUnreachable: "Datenbank nicht erreichbar",
    database: "Datenbank",
    status: "Status",
    latency: "Latenz",
    errorLabel: "Fehler",
    tableRows: "{{table}}-Zeilen",
    server: "Server",
    uptime: "Betriebszeit",
    memory: "Speicher",
    env: "Umgebung",
    diagTime: "Diag-Zeit",
    pmIntegrationsConnected: "PM-Integrationen verbunden",
    pmIntegrationConnected: "PM-Integration verbunden",
    schemaDriftTables: "Schema-Abweichung — {{count}} Tabellen",
    schemaDriftTable: "Schema-Abweichung — {{count}} Tabelle",
    missing: "fehlend",
    run: "Führen Sie",
    toFix: "aus, um zu beheben.",
    missingTables: "Fehlende Tabellen ({{count}})",
    toCreateMissingTables: "aus, um fehlende Tabellen zu erstellen.",
  },
  projectGrid: {
    opening: "Wird geöffnet…",
    lastUpdated: "Zuletzt aktualisiert: {{date}}",
    notCreated: "nicht erstellt",
    noAudit: "Kein Audit",
    editAuditType: "Audit-Typ bearbeiten",
    outstandingTasks: "Offene Aufgaben",
    complete: "Abgeschlossen",
    inProgress: "In Bearbeitung",
    taskProgress: "{{total}} Aufgaben - {{completed}} abgeschlossen",
    notStarted: "Nicht begonnen",
    failedOpenOverview: "Übersichts-Editor konnte nicht geöffnet werden",
    failedOpenOverviewStatus: "Übersichts-Editor konnte nicht geöffnet werden ({{status}})",
    titleProjectOverview: "Projektübersicht",
    titleProjectLifecycle: "Projekt-Lifecycle",
    titleDevelopmentPlan: "Entwicklungsplan",
    titleAuditReport: "Prüfbericht",
    titleCompoundLearnings: "Kumulierte Erkenntnisse",
    titleProjectScopeAnalysis: "Projekt-Scope-Analyse",
    titleEntityDiagram: "Entitätsdiagramm",
    showDocuments: "Dokumente anzeigen",
    hideDocuments: "Dokumente ausblenden",
    newProject: "Neu",
    allProjects: "Alle Projekte",
    active: "Aktiv",
    branched: "Verzweigt",
    emptyAdmin: "Noch keine aktiven Projekte. Klicken Sie auf Neu, um Ihr erstes Projekt zu erstellen.",
    emptyTeam: "Ihnen sind noch keine Projekte zugewiesen.",
  },
  phaseTabBar: {
    assignedUser: "Zugewiesener Benutzer",
  },
  designPhase: {
    specifications: "Spezifikationen",
    clarifiedCount: "{{clarified}} / {{total}} geklärt",
  },
  implementationPhase: {
    tasksHeading: "Aufgaben",
    openCount: "{{n}} offen",
  },
  regression: {
    loading: "Vergleich mit früheren Läufen…",
    failed: "Vergleich konnte nicht erstellt werden",
    title: "Regressionsprüfung für {{version}}",
    blocking: "{{count}} blockierend",
    newlyFailing: "Neu fehlgeschlagen",
    newlyFailingHint: "Beim letzten Lauf bestanden. Das hält das Release auf.",
    neverPassed: "Nie bestanden",
    neverPassedHint: "Offene Arbeit, keine Regression.",
    stillPassing: "Weiterhin bestanden",
    lastPassed: "Zuletzt bestanden in {{version}}",
  },
  testSuite: {
    skipTitle: "Testanforderung",
    skipOn: "Tests sind für dieses Projekt DEAKTIVIERT.",
    skipOff: "Tests sind für dieses Projekt erforderlich.",
    skipTurnOn: "Tests überspringen",
    skipReEnable: "Tests wieder verlangen",
    skipCancel: "Abbrechen",
    skipConfirm: "Damit wird die Release-Sperre für JEDEN Bereich dieses Projekts deaktiviert. Pflichttests blockieren nicht mehr, und Releases gelten als übersprungen statt als getestet. Dies wird unter Ihrem Namen protokolliert.",
    skipConfirmYes: "Ja, Tests überspringen",
    skipHint: "Nur Administratoren. Die Änderung wird im Audit-Log festgehalten.",
    skipBannerTitle: "Tests sind für dieses Projekt deaktiviert",
    skipBannerBody: "Pflichttests blockieren keine Releases. Gates melden „übersprungen“, nicht „bestanden“.",
    scopeFilter: "Bereich:",
    allScopes: "Alle Bereiche ({{count}})",
    loading: "Testsuite wird geladen…",
    loadFailed: "Testsuite konnte nicht geladen werden",
    runFailed: "Testlauf konnte nicht gestartet werden",
    empty: "Noch keine Testfälle. Sie entstehen bei der Umsetzung des Scopes.",
    cycle: "Version {{version}}",
    cycleStatus: { open: "In Entwicklung", uat: "In UAT", production: "In Produktion", superseded: "Abgelöst" },
    humanPending: "{{count}} Benutzertest(s) warten auf die UAT-Freigabe",
    runGate: "{{gate}} ausführen",
    noItemsForGate: "Keine Testfälle im Gate {{gate}}",
    runRunning: "Testlauf läuft",
    runStalled: "Testlauf hängt — keine Rückmeldung vom Runner",
    runTotals: "{{passed}} bestanden · {{failed}} fehlgeschlagen · {{total}} gesamt",
    required: "Pflicht",
    origin: { ai: "KI", user: "Tester" },
    inherit: "Erbt", override: "Überschreiben",
    forceInherited: "Übernimmt die Pflichteinstellung des Scopes",
    forceOverridden: "Überschreibt die Pflichteinstellung des Scopes",
    forceScopeTitle: "Test verpflichtend",
    forceScopeLabel: "Freigabe für alle {{count}} Tests dieses Scopes verlangen",
    forceScopeHint: "Wenn aktiv, kann das Produkt ohne Freigabe dieser Tests nicht in Richtung Produktion gehen.",
    filter: { all: "Alle", outstanding: "Offen", forced: "Pflicht", failing: "Fehlerhaft" },
  },
  userTest: {
    loading: "Ihre Tests werden geladen…",
    loadFailed: "Ihre Tests konnten nicht geladen werden",
    signoffFailed: "Konnte nicht gespeichert werden",
    dismissFailed: "Erneuter Test konnte nicht verworfen werden",
    notReleasedTitle: "Noch nicht bereit für Sie",
    notReleasedBody: "{{count}} Test(s) warten. Sie werden freigegeben, sobald die automatischen Tests dieser Version bestanden sind.",
    summary: "{{required}} Pflicht · {{optional}} optional · Version {{version}}",
    required: "Pflicht", optional: "Optional",
    testerAuthored: "Von einem Tester ergänzt",
    addTest: "Test hinzufügen",
    preconditions: "Vorbereitung", steps: "Schritte", postconditions: "Danach prüfen",
    action: "Das tun", expected: "Das sollten Sie sehen",
    pass: "Bestanden", fail: "Fehlgeschlagen",
    notesPlaceholder: "Was ist passiert? (bei Fehlschlag erforderlich)",
    failNeedsReason: "Beschreiben Sie, was tatsächlich passiert ist — daraus entsteht der Fehlerbericht.",
    failHint: "Ein Fehlschlag erzeugt einen Fehlerbericht mit diesen Schritten.",
    bugFiled: "Fehlerbericht mit Ihren Schritten und Beobachtungen erstellt.",
    viewBug: "Fehlerbericht öffnen",
    retestTitle: "Bitte erneut testen",
    retestImpact: "Der von diesem Test abgedeckte Code hat sich geändert: {{path}} ({{hops}} Schritt(e) entfernt).",
    retestBroad: "Eine weitreichende Änderung wurde eingespielt, daher werden alle Benutzertests erneut geprüft.",
    retestUndetermined: "Wir konnten nicht feststellen, ob die Änderung diesen Test betrifft — daher die Nachfrage.",
    dismissRetest: "Nicht betroffen",
    dismissExplain: "Wenn die Änderung Ihr Ergebnis nicht beeinflussen kann, begründen Sie das. Ihre frühere Freigabe bleibt bestehen.",
    dismissPlaceholder: "Warum diese Änderung den Test nicht betrifft",
    confirmDismiss: "Erneuten Test verwerfen",
  },
  addTest: {
    title: "Test aus Ihrer Beobachtung anlegen",
    intro: "Sie haben etwas gefunden, das die Spezifikation nicht vorgesehen hat. Halten Sie es fest, damit es bei jedem Release geprüft wird.",
    whatHappened: "Was zutreffen soll",
    titlePlaceholder: "z. B. Phasenwechsel bei laufendem Timer hält die Dauer korrekt",
    setup: "Vorbereitung (eine pro Zeile)",
    setupPlaceholder: "z. B. Ein Projekt mit laufendem Timer",
    steps: "Schritte",
    actionPlaceholder: "Das tun…",
    expectedPlaceholder: "Das sollten Sie sehen… (Wert, Anzahl, Meldung)",
    addStep: "Schritt hinzufügen",
    after: "Danach prüfen (eine pro Zeile)",
    afterPlaceholder: "z. B. Die Summe der Zeiterfassung ist unverändert",
    vagueWarning: "Schritt(e) {{steps}} sagen sinngemäß \"funktioniert\". Beschreiben Sie, was konkret zu sehen sein soll — Wert, Anzahl oder Meldung.",
    needTitle: "Geben Sie dem Test einen Titel",
    needStep: "Fügen Sie mindestens einen Schritt hinzu",
    needExpected: "Jeder Schritt braucht ein erwartetes Ergebnis",
    failed: "Test konnte nicht gespeichert werden",
    cancel: "Abbrechen", save: "Test speichern",
  },
  qaPhase: {
    testTasks: "Testaufgaben",
    passCount: "{{n}} bestanden",
    failCount: "{{n}} fehlgeschlagen",
  },
  humanTestingPhase: {
    feedbackItems: "Feedback-Elemente",
    issuesRaisedByTesters: "Von menschlichen Testern gemeldete Probleme",
    openCount: "{{count}} offen",
  },
  betaPhase: {
    approvalTitle: "Beta-Phase — Genehmigung durch Administrator erforderlich",
    approvalBody: "Das Erstellen neuer Aufgaben während der Beta-Phase erfordert die ausdrückliche Genehmigung eines Administrators. Dies wird im Audit-Protokoll festgehalten.",
    approveAndCreate: "Genehmigen und erstellen",
    feedbackTitle: "Beta-Feedback",
    feedbackHint: "Änderungen erfordern die Genehmigung eines Administrators",
    openCount: "{{count}} offen",
    creatingApprovedTask: "Genehmigte Aufgabe wird erstellt...",
  },
  releasePhase: {
    statusComplete: "Abgeschlossen",
    statusInProgress: "In Bearbeitung",
    statusReopened: "Wieder geöffnet",
    statusPending: "Ausstehend",
    manualDeployment: "Manuelle Bereitstellung",
    ciCdPipeline: "CI/CD-Pipeline",
    stagedRollout: "Gestaffelte Einführung",
    hotfix: "Hotfix",
    releaseRecord: "Release-Datensatz",
    priorPhasesIncomplete: "Vorherige Phasen unvollständig",
    versionNumber: "Versionsnummer",
    versionPlaceholder: "z. B. v1.0.0",
    releaseDate: "Release-Datum",
    releaseMethod: "Release-Methode",
    releaseNotes: "Release-Hinweise",
    notesPlaceholder: "Fassen Sie zusammen, was veröffentlicht wurde, wichtige Änderungen, bekannte Probleme...",
    saved: "Gespeichert",
    saveNotes: "Notizen speichern",
    fullLifecycleSummary: "Vollständige Lebenszyklus-Übersicht",
    columnPhase: "Phase",
    columnStatus: "Status",
    columnEntered: "Begonnen",
    columnCompleted: "Abgeschlossen",
    projectLabel: "Projekt",
  },
  phaseCompletionBar: {
    advanceFailed: "Phase konnte nicht weitergeschaltet werden",
    reopenFailed: "Phase konnte nicht wieder geöffnet werden",
    checkingCriteria: "Abschlusskriterien werden geprüft...",
    phaseComplete: "Phase abgeschlossen",
    phaseReopened: "Phase wieder geöffnet",
    readyToAdvance: "Bereit zum Weiterschalten",
    criteriaMet: "{{pass}} von {{total}} Kriterien erfüllt",
    collapse: "Einklappen",
    expand: "Ausklappen",
    advancing: "Wird weitergeschaltet...",
    markAsReleased: "Als veröffentlicht markieren",
    advancePhase: "Phase weiterschalten",
    reopenedForRevisions: "Diese Phase wurde für Überarbeitungen wieder geöffnet.",
    cancelReopen: "Wiederöffnen abbrechen",
    reopenPhase: "Phase wieder öffnen",
    reopenReasonPlaceholder: "Grund für das Wiederöffnen dieser Phase...",
    reopening: "Wird wieder geöffnet...",
    confirmReopen: "Wiederöffnen bestätigen",
  },
  phaseTaskGroup: {
    unassigned: "Nicht zugewiesen",
    unnamedScope: "Unbenannter Scope",
    noTasksInScope: "Keine Aufgaben in diesem Scope.",
    noTasksInPhase: "Keine Aufgaben in dieser Phase.",
    unscopedTasks: "Aufgaben ohne Scope",
    failedToLoad: "Aufgaben konnten nicht geladen werden",
    requestFailed: "Anfrage fehlgeschlagen: {{status}}",
  },
  taskList: {
    statusOpen: "Offen",
    statusInProgress: "In Bearbeitung",
    statusCompleted: "Abgeschlossen",
    statusDismissed: "Verworfen",
    resultNone: "—",
    resultPass: "Bestanden",
    resultFail: "Fehlgeschlagen",
    resultBlocked: "Blockiert",
    priorityLow: "Niedrig",
    priorityMed: "Mittel",
    priorityHigh: "Hoch",
    priorityCrit: "Krit.",
    taskNamePlaceholder: "Aufgabenname...",
    descriptionPlaceholder: "Beschreibung (optional)...",
    addTaskButton: "Aufgabe hinzufügen",
    addTask: "Aufgabe hinzufügen",
    createError: "Aufgabe konnte nicht erstellt werden",
    noTasks: "Noch keine Aufgaben",
    colTask: "Aufgabe",
    colResult: "Ergebnis",
    colRaisedBy: "Gemeldet von",
    colPriority: "Priorität",
    colStatus: "Status",
    countOpen: "{{n}} offen",
    countClosed: "{{n}} geschlossen",
    countTotal: "{{n}} gesamt",
  },
  phase: {
    design: "Entwurf",
    implementation: "Implementierung",
    qa: "QA",
    humanTesting: "Menschliche Tests",
    beta: "Beta",
    release: "Freigabe",
  },
  projectPanel: {
    saveFailed: "Speichern fehlgeschlagen",
    failedToLoad: "Laden fehlgeschlagen",
    loadingTasks: "Aufgaben werden geladen…",
    noTasksForScope: "Keine Aufgaben für diesen Scope.",
    filterAll: "alle",
    filterOpen: "offen",
    filterClosed: "geschlossen",
    noFilterTasks: "Keine {{filter}} Aufgaben.",
    openTasks: "Offene Aufgaben",
    noOpenTasks: "Keine offenen Aufgaben.",
    statTotal: "Gesamt",
    statOpen: "Offen",
    statClosed: "Geschlossen",
    statInProgress: "In Bearbeitung",
    statComplete: "Abgeschlossen",
    statActive: "Aktiv",
    statResolved: "Gelöst",
    statScopesPassing: "Bestandene Scopes",
    statQaTasksDone: "QA-Aufgaben erledigt",
    statNoTasks: "Keine Aufgaben",
    documents: "Dokumente",
    completeCheck: "✓ abgeschlossen",
    tasksCount: "Aufgaben ({{done}}/{{total}})",
    specOpenClosed: "Spec {{num}}: Offen {{open}} · Geschlossen {{closed}}",
    // [SCOPE 137 / T014] Change Impact agreement log (read-only)
    changeImpactTitle: "Change-Impact-Protokoll",
    changeImpactCount: "Change-Impact-Protokoll ({{count}})",
    changeImpactLoading: "Wird geladen…",
    changeImpactEmpty: "Für diesen Scope wurden keine Zustimmungen oder Überschreitungen erfasst.",
    changeImpactError: "Das Change-Impact-Protokoll konnte nicht geladen werden.",
    changeImpactKindAck: "Bestätigt",
    changeImpactKindApproval: "Genehmigt",
    changeImpactKindDeclined: "Abgelehnt",
    changeImpactKindCrossing: "Unbeaufsichtigt überschritten",
    changeImpactAffected: "{{count}} betroffen",
    changeImpactQueued: "nach einer Offline-Phase gesendet",
    changeImpactReadOnly: "Schreibgeschützt. Diese Einträge können nicht geändert werden.",
    reopen: "Erneut öffnen",
    reactivate: "Reaktivieren",
    markClarified: "Als geklärt markieren",
    defer: "Zurückstellen",
    needsClarification: "Klärung erforderlich",
    deleteScope: "Löschen",
    deleteScopeConfirm: "Scope {{num}} \"{{title}}\" und seine {{tasks}} Aufgabe(n) und {{docs}} Dokument(e) dauerhaft löschen? Dies kann nicht rückgängig gemacht werden.",
    deleteScopeError: "Der Scope konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.",
    overviewDoc: "Übersichtsdokument",
    scopeCountOne: "{{n}} Scope",
    scopeCountOther: "{{n}} Scopes",
    fullyGenerated: "{{n}} vollständig generiert",
    noScopesCreatedYet: "Noch keine Scopes erstellt.",
    scopeDocuments: "Scope-Dokumente",
    noScopes: "Keine Scopes.",
    progressByScope: "Fortschritt nach Scope",
    qaStatusByScope: "QA-Status nach Scope",
    allTestsPassed: "Alle {{n}} Tests bestanden",
    inProgressDone: "{{n}} in Bearbeitung · {{done}}/{{total}} erledigt",
    completeCount: "{{done}}/{{total}} abgeschlossen",
    noQaTasks: "Keine QA-Aufgaben",
    betaApprovalNotice: "Neue Aufgaben in der Beta erfordern vor der Erstellung die Genehmigung eines ADMIN.",
    feedbackByScope: "Feedback nach Scope",
    allScopesClear: "Alle Scopes frei — bereit zur Veröffentlichung.",
    openItemsRemain: "{{n}} offene Elemente verbleiben über die Scopes hinweg.",
    phaseChecklist: "Phasen-Checkliste",
    openItemsByScope: "Offene Elemente nach Scope",
    openResolved: "{{open}} offen · {{done}}/{{total}} gelöst",
    phaseView: "{{phase}}-Ansicht",
    allProjects: "Alle Projekte",
    open: "Öffnen",
    status: {
      draft: "Entwurf",
      inReview: "In Prüfung",
      pending: "Ausstehend",
      clarified: "Geklärt",
      planned: "Geplant",
      tasksGenerated: "Aufgaben erstellt",
      implemented: "Implementiert",
      qaPassed: "QA bestanden",
      humanTested: "Manuell getestet",
      betaCleared: "Beta freigegeben",
      released: "Veröffentlicht",
      deferred: "Zurückgestellt",
    },
    doctype: {
      tasks: "Aufgaben",
      datamodel: "Datenmodell",
      quickstart: "Schnellstart",
      checklist: "Checkliste",
      contracts: "Verträge",
      research: "Recherche",
      overview: "Übersicht",
      stackStyle: "Stack & Stil",
    },
    issuetype: {
      bug: "Fehler",
      feature: "Funktion",
      enhancement: "Verbesserung",
      task: "Aufgabe",
    },
  },
  helpCatalog: {
    ui: {
      included: "Enthalten",
      upgrade: "Upgrade",
      admin: "Admin",
      adminOnly: "Nur Administrator",
      howToLabel: "So geht's:",
      howToUseIt: "So verwenden Sie es",
      availableFromPrefix: "Verfügbar ab dem Tarif ",
      availableFromSuffix: ".",
      yourPlan: "Ihr Tarif",
      subscriptionPlan: "Abonnement-Tarif",
    },
    category: {
      coreWorkspace: "Kern-Arbeitsbereich",
      developmentLifecycle: "Entwicklungslebenszyklus",
      integrations: "Integrationen",
      teamCollaboration: "Team & Zusammenarbeit",
      timeBillingConsulting: "Zeit, Abrechnung & Beratung",
      adminConfiguration: "Verwaltung & Konfiguration",
      securityCompliance: "Sicherheit & Compliance",
      enterpriseSupport: "Enterprise & Support",
    },
    plan: {
      SINGLE_USER: {
        label: "Einzelnutzer",
        userLimit: "1 Nutzer",
      },
      SMALL_BUSINESS: {
        label: "Kleine/Mittlere Unternehmen",
        userLimit: "Bis zu 5 Nutzer",
      },
      CONSULTANT: {
        label: "Berater",
        userLimit: "Unbegrenztes Team",
      },
      ENTERPRISE: {
        label: "Enterprise",
        userLimit: "25+ Nutzer",
      },
    },
    feature: {
      dashboard: {
        title: "Dashboard",
        description: "Ihre Projektübersicht — der Fortschritt der Lifecycle-Phasen pro Projekt sowie Schnellzugriffe auf die generierten Dokumente jedes Projekts.",
        howTo: "Öffnen Sie das Dashboard über die Seitenleiste. Jede Projektkarte zeigt ihren Phasenfortschritt und Dokumentenlinks; ADMINs erhalten eine Schaltfläche „Neu“, um ein Projekt direkt anzulegen.",
      },
      projects: {
        title: "Projekte",
        description: "Erstellen und verwalten Sie Projekte. Unbegrenzte Projekte in allen kostenpflichtigen Tarifen.",
        howTo: "Erstellen Sie ein Projekt über die Schaltfläche „Neu“ im Dashboard oder das Menü „Projekte“ und öffnen Sie es dann, um seine Registerkarten (Übersicht, Lifecycle, Aufgaben) zu sehen.",
      },
      tasks: {
        title: "Meine Aufgaben (Kanban)",
        description: "Ihre zugewiesene Arbeit auf einem projektübergreifenden Kanban-Board.",
        howTo: "Öffnen Sie „Meine Aufgaben“; ziehen Sie Karten zwischen den Spalten, um den Status zu ändern, oder öffnen Sie eine Karte für Details.",
      },
      settings: {
        title: "Einstellungen",
        description: "Konto-, Unternehmens- und Präferenzeinstellungen, einschließlich der Sprache.",
        howTo: "Öffnen Sie die Einstellungen über das Menü; hier befinden sich die Sprachauswahl und das Profil.",
      },
      faq: {
        title: "FAQ",
        description: "Antworten auf häufige Fragen zur Nutzung von wxKanban.",
        howTo: "Öffnen Sie die FAQ über das Menü.",
      },
      lifecycle: {
        title: "6-Phasen-Lifecycle und Orchestrator",
        description: "Die Methodik Design → Implementation → QA → HumanTesting → Beta → Release, die jede Funktion steuert und eine einzige verlässliche Quelle für den Stand eines Projekts vorhält.",
        howTo: "Steuern Sie es über Claude Code + das wxKanban-Kit in VS Code (buildscope → createSpecs → implement). Der richtige Befehl ist nur in der richtigen Phase verfügbar.",
      },
      specs: {
        title: "Specs und Scopes",
        description: "Die Scope-of-project-Dokumente sowie die generierten Specs, Pläne und Aufgaben für jede Funktion.",
        howTo: "Verfassen Sie einen Scope mit buildscope; generieren Sie Spec, Plan und Aufgaben mit createSpecs; verfolgen Sie sie im Cockpit.",
      },
      audit: {
        title: "Audit-Berichte",
        description: "Ein Audit-Verlauf pro Release darüber, was gebaut, getestet und mit Code-Fences versehen wurde.",
        howTo: "Öffnen Sie den Link „Audit-Bericht“ auf einer Projektkarte oder generieren Sie ihn im Admin-Dashboard neu.",
      },
      cockpit: {
        title: "Dev Cockpit",
        description: "Eine VS-Code-Seitenleiste, die die verbleibende Arbeit pro Scope in Echtzeit anzeigt, während die Arbeit voranschreitet.",
        howTo: "Installieren Sie die Dev-Cockpit-Erweiterung; sie heftet Ihren aktiven Scope an und listet unerledigte Aufgaben auf.",
      },
      pmIntegrations: {
        title: "PM-Integrationen",
        description: "Verbinden Sie Jira, Monday.com, Asana und Trello und synchronisieren Sie Aufgaben in beide Richtungen.",
        howTo: "Öffnen Sie PM-Integrationen und verbinden Sie sich über OAuth; die Synchronisierung läuft nach einem Hintergrundzeitplan.",
      },
      team: {
        title: "Teamverwaltung",
        description: "Laden Sie Teammitglieder ein, weisen Sie Rollen zu und verwalten Sie den Zugriff mit rollenbasierten Kontrollen.",
        howTo: "Öffnen Sie „Team“; laden Sie per E-Mail ein und legen Sie die Rolle jedes Mitglieds fest.",
      },
      timeTracking: {
        title: "Zeiterfassung und Abrechnung",
        description: "Integrierter Timer mit Erfassung abrechenbarer Stunden und Zeiteinträgen.",
        howTo: "Starten Sie den Timer für eine Aufgabe; markieren Sie Einträge als abrechenbar für die Rechnungsstellung.",
      },
      invoices: {
        title: "Rechnungen",
        description: "Erstellen Sie PDF-Rechnungen aus abrechenbaren Zeiteinträgen.",
        howTo: "Öffnen Sie „Rechnungen“, wählen Sie ein Projekt und einen Zeitraum und generieren Sie; abgerechnete Einträge werden markiert, um Duplikate zu vermeiden.",
      },
      reports: {
        title: "Berichte",
        description: "Erweiterte Berichte zu Zeit, Projekten und Ergebnissen.",
        howTo: "Öffnen Sie „Berichte“ über das Menü.",
      },
      workHub: {
        title: "Work Hub",
        description: "Ein Auftrags-Hub für Berater zur Verwaltung der Kundenarbeit.",
        howTo: "Öffnen Sie den Work Hub, um Ihre Aufträge einzusehen und zu verwalten.",
      },
      clientPortal: {
        title: "Kundenportal",
        description: "Geben Sie Kunden eine eingeschränkte, schreibgeschützte Ansicht ihres Projekts.",
        howTo: "Aktivieren Sie das Portal für ein Projekt und teilen Sie den Kundenlink.",
      },
      customWorkflows: {
        title: "Benutzerdefinierte Workflows",
        description: "Passen Sie die Lifecycle-Workflows an den Prozess Ihres Teams an.",
        howTo: "Konfigurieren Sie benutzerdefinierte Workflows in den Projekteinstellungen.",
      },
      apiAccess: {
        title: "API-Zugriff",
        description: "Programmatischer Zugriff auf Ihre wxKanban-Daten über die API.",
        howTo: "Erstellen Sie ein API-Token in den Einstellungen und rufen Sie die dokumentierten Endpunkte auf.",
      },
      stackProfiles: {
        title: "Stack-Profile",
        description: "Wiederverwendbare Ziel-Stack- und Stildefinitionen, die bei der Erstellung eines Projekts angewendet werden.",
        howTo: "Öffnen Sie die Stack-Profile (Admin), um die Stacks Ihres Unternehmens zu definieren oder zu bearbeiten.",
      },
      themeProfiles: {
        title: "Theme-Profile",
        description: "Wiederverwendbare Erscheinungsbild-Themes für generierte Ausgaben.",
        howTo: "Öffnen Sie die Theme-Profile (Admin), um Themes zu definieren oder zu bearbeiten.",
      },
      encryption: {
        title: "Verschlüsselung im Ruhezustand",
        description: "Geheimnisse werden im Ruhezustand mit AES-256-GCM verschlüsselt.",
        howTo: "Immer aktiv – keine Konfiguration erforderlich.",
      },
      soc2: {
        title: "SOC 2 & HIPAA",
        description: "An SOC 2 und HIPAA ausgerichtete Kontrollen und Compliance-Tools.",
        howTo: "Fügen Sie die SDLC-Dokumente Ihres Unternehmens für die Compliance-Analyse hinzu und weisen Sie Teammitgliedern Kontrollen zu.",
      },
      sso: {
        title: "Single Sign-On (SSO)",
        description: "Unternehmens-SSO für Ihre Organisation.",
        howTo: "Wenden Sie sich an Ihren wxKanban-Ansprechpartner, um Ihren Identitätsanbieter zu konfigurieren.",
      },
      advancedSecurity: {
        title: "Erweiterte Sicherheit",
        description: "Zusätzliche Sicherheitskontrollen auf Unternehmensniveau.",
        howTo: "Wird im Rahmen des Enterprise-Onboardings konfiguriert.",
      },
      emailSupport: {
        title: "E-Mail-Support",
        description: "Produktsupport per E-Mail.",
        howTo: "Senden Sie eine E-Mail an admin@wxperts.com.",
      },
      dedicatedSupport: {
        title: "Dedizierter Support",
        description: "Ein dedizierter Support-Ansprechpartner für Ihr Konto.",
        howTo: "Wird während des Enterprise-Onboardings zugewiesen.",
      },
      customIntegrations: {
        title: "Benutzerdefinierte Integrationen",
        description: "Maßgeschneiderte Integrationen für Ihre Organisation.",
        howTo: "Legen Sie den Umfang benutzerdefinierter Integrationen mit Ihrem wxKanban-Ansprechpartner fest.",
      },
      sla: {
        title: "SLA-Garantie",
        description: "Ein vertragliches SLA für Verfügbarkeit und Reaktionszeit.",
        howTo: "Bestandteil Ihres Enterprise-Vertrags.",
      },
    },
  },
  // [SCOPE 052] Öffentliche Hilfe/Tutorials-Seiten (/docs, /docs/:slug)
  docs: {
    title: "Hilfe/Tutorials",
    backHome: "← Zurück zur Startseite",
    sectionsLabel: "Hilfebereiche",
    navFaq: "FAQ",
    navVideos: "Videos",
    navSessions: "Schulungen",
    navGuides: "Leitfäden",
    guidesHeading: "Leitfäden",
    guidesSubtitle: "Anleitungen zum Herunterladen, die Sie neben Ihrem Projekt aufbewahren können.",
    guidesConversionTitle: "WINDEV / WEBDEV Migrationsleitfaden",
    guidesConversionSummary: "Wie Sie eine bestehende WINDEV- oder WEBDEV-Anwendung Schritt für Schritt auf einen modernen React- und PostgreSQL-Stack übertragen.",
    guidesDraftBadge: "Entwurf",
    guidesDraftNote: "Dieser Leitfaden ist noch ein Entwurf. Die Schritte sind nutzbar, Formulierungen und Details können sich jedoch ändern.",
    guidesLoading: "Anleitungen werden geladen…",
    guidesEmpty: "Es sind noch keine Anleitungen verfügbar.",
    guidesLanguageLabel: "Sprache",
    guidesLanguageAll: "Alle Sprachen",
    faqHeading: "Häufig gestellte Fragen",
    faqSubtitle: "Antworten auf häufige Fragen zu wxKanban.",
    faqEmpty: "Es sind noch keine FAQ verfügbar.",
    sessionsHeading: "Kommende Online-Schulungen",
    sessionsSubtitle: "Nehmen Sie an einer Live-Schulung mit einem Trainer teil, um schnell loszulegen.",
    sessionsEmpty: "Derzeit sind keine Schulungen geplant.",
    sessionsRegister: "Bekunden Sie Ihr Interesse, und wir benachrichtigen Sie, sobald die nächste Schulung öffnet.",
    sessionsCta: "Kontaktieren Sie uns zur Anmeldung",
    sessionsJoin: "Anmelden",
    videosHeading: "Schulungsvideos",
    videosSubtitle: "Sehen Sie sich Schritt-für-Schritt-Anleitungen zu wxKanban an.",
    videosEmpty: "Es sind noch keine Schulungsvideos verfügbar.",
    loading: "Wird geladen…",
    loadError: "Die Dokumentation konnte nicht geladen werden. Bitte versuchen Sie es später erneut.",
    backAll: "← Gesamte Dokumentation",
    detailError: "Dieses Dokument konnte nicht geladen werden. Bitte versuchen Sie es später erneut.",
    notFoundTitle: "Nicht gefunden",
    notFoundBody: "Dieses Dokument existiert nicht oder ist noch nicht veröffentlicht.",
  },
  // [SCOPE 131 / T013] Suspend & cancel copy (FR-011, FR-009).
  subscriptionLifecycle: {
    sectionTitle: "Aussetzen oder kündigen",
    sectionDesc: "Pausieren Sie die Abrechnung für eine Weile oder kündigen Sie Ihr Abonnement zum Ende des bereits bezahlten Zeitraums.",
    stateActive: "Ihr Abonnement ist aktiv.",
    statePaused: "Die Abrechnung ist ausgesetzt.",
    statePausedDetail: "Es werden keine Rechnungen gestellt und es wurden keine Daten gelöscht. Nehmen Sie sie wieder auf, wann immer Sie möchten — Ihre Projekte und Ihr Verlauf warten auf Sie.",
    stateCancelPending: "Kündigung vorgemerkt.",
    stateCancelPendingDetail: "Sie behalten den vollen Zugriff bis zum {{date}}. Bis dahin können Sie die Kündigung jederzeit zurücknehmen.",
    effectiveDateUnknown: "das Ende Ihres laufenden Abrechnungszeitraums",
    pauseTitle: "Abrechnung aussetzen",
    pauseDesc: "Stripe stellt Ihnen nichts mehr in Rechnung und Ihr Team verliert den Zugriff, bis Sie fortfahren. Nichts wird gelöscht und nichts zur Löschung vorgemerkt.",
    pauseAction: "Abrechnung aussetzen",
    unpauseAction: "Abrechnung fortsetzen",
    cancelTitle: "Abonnement kündigen",
    cancelDesc: "Ihr Abonnement endet, wenn der bereits bezahlte Zeitraum abläuft. Bis dahin behalten Sie den vollen Zugriff.",
    cancelAction: "Abonnement kündigen",
    undoCancelAction: "Abonnement behalten",
    pauseSuccess: "Abrechnung ausgesetzt. Nehmen Sie sie wieder auf, wann immer Sie möchten.",
    unpauseSuccess: "Abrechnung fortgesetzt — willkommen zurück.",
    cancelSuccess: "Ihr Abonnement endet am {{date}}. Bis dahin können Sie das zurücknehmen.",
    undoCancelSuccess: "Kündigung zurückgenommen. Ihr Abonnement läuft normal weiter.",
    actionFailed: "Das hat nicht funktioniert, und es wurde nichts geändert. Bitte versuchen Sie es erneut.",
    dialogTitle: "Abonnement kündigen?",
    dialogBody: "Ihr Abonnement bleibt bis zum {{date}} aktiv. Danach verliert Ihr Team den Zugriff, und 30 Tage später werden die Daten Ihres Unternehmens archiviert.",
    dialogUndoNote: "Sie können das jederzeit vor dem {{date}} zurücknehmen.",
    dialogTypeLabel: "Geben Sie {{name}} ein, um zu bestätigen",
    dialogPlaceholder: "Firmenname",
    dialogConfirm: "Abonnement kündigen",
    dialogKeep: "Abonnement behalten",
    dialogMismatch: "Das stimmt nicht mit Ihrem Firmennamen überein.",
    wallPausedTitle: "Die Abrechnung ist ausgesetzt",
    wallPausedBody: "Ihr Unternehmen hat seine eigene Abrechnung ausgesetzt, daher pausiert das Produkt. Es wurde nichts gelöscht. Ein Administrator kann sie unter Einstellungen → Abrechnung und Abonnement fortsetzen.",
    wallPausedCta: "Zu den Abrechnungseinstellungen",
    wallPausedNonAdmin: "Ihr Unternehmen hat die Abrechnung ausgesetzt. Es wurde nichts gelöscht — bitten Sie Ihren Administrator, sie fortzusetzen.",
    emailSubject: "Ihr wxKanban-Abonnement endet am {{date}}",
    emailHeading: "Ihre Kündigung ist bestätigt",
    emailBody: "Wir haben Ihr wxKanban-Abonnement zum {{date}} beendet. Bis zu diesem Datum behalten Sie den vollen Zugriff auf {{company}}, und es wird Ihnen nichts mehr berechnet.",
    emailUndoHeading: "Anders entschieden?",
    emailUndoBody: "Sie können das jederzeit vor dem {{date}} unter Einstellungen → Abrechnung und Abonnement zurücknehmen. Solange Ihre Kündigung aussteht, wird nichts gelöscht.",
    emailFooter: "Falls Sie das nicht veranlasst haben, wenden Sie sich umgehend an den Administrator Ihres Unternehmens.",
  },
  // [SCOPE 005 / T019] Time & Billing (SPEC-005). "Resource", never "AI" (constraint 5).
  timeBilling: {
    common: {
      cancel: "Abbrechen",
      close: "Schließen",
      delete: "Löschen",
      edit: "Bearbeiten",
      loading: "Wird geladen…",
      save: "Speichern",
      saving: "Wird gespeichert…",
    },
    page: {
      title: "Zeit und Abrechnung",
      open: "Zeit und Abrechnung öffnen",
      manualOnly: "Die Zeit von Ressourcen aus Agentenläufen ist noch nicht verfügbar. Erfassen Sie die Zeit von Ressourcen manuell.",
      engagedManual: "Bei Kundenprojekten, in denen Sie eingesetzt sind, wird die Zeit von Ressourcen nur manuell erfasst.",
      generateStatement: "Abrechnung erstellen",
      billedTotal: "Abgerechnete Projekte in diesem Zeitraum: {{hours}} Std.",
      actionFailed: "Das hat nicht funktioniert. Bitte versuchen Sie es erneut.",
      loadFailed: "Ihre Projekte konnten nicht geladen werden.",
    },
    range: {
      from: "Von",
      to: "Bis",
      thisWeek: "Diese Woche",
      includeInvoiced: "Bereits abgerechnete Zeit einbeziehen",
      invalid: "Das Startdatum darf nicht nach dem Enddatum liegen.",
    },
    projects: {
      none: "Noch keine Projekte.",
      clientProject: "Kunde: {{client}}",
      hours: "{{hours}} Std.",
      billThis: "Abrechenbar",
      entries: "Einträge",
      rates: "Sätze",
      client: "Kunde",
      noClient: "— Kein Kunde —",
      billedTo: "Abgerechnet an {{client}}",
      assignClient: "Weisen Sie einen Kunden zu, um dieses Projekt abzurechnen",
    },
    lines: {
      line: "Zeile",
      scope: "Scope",
      developer: "Entwickler",
      resource: "Ressource",
      developerHours: "Std. Entwickler",
      resourceHours: "Std. Ressource",
      actions: "Aktionen",
      addDeveloper: "Entwicklerzeit hinzufügen",
      addResource: "Ressourcenzeit hinzufügen",
      noScopes: "Dieses Projekt hat noch keine Scopes.",
      deletedScope: "Gelöschter Scope",
      archived: "archiviert",
      brainstorming: "Brainstorming",
      processes: "Prozesse",
      dba: "DBA",
      management: "Management",
      meetings: "Besprechungen",
      projectTotal: "Projektsumme",
    },
    entry: {
      addDeveloper: "Entwicklerzeit hinzufügen",
      addResource: "Ressourcenzeit hinzufügen",
      editTitle: "Zeiteintrag bearbeiten",
      person: "Wer hat die Arbeit erledigt",
      model: "Modell",
      date: "Datum",
      time: "Zeit",
      timeHelp: "Geben Sie 1:38, 1.5 oder 45m ein. Ein führendes Minuszeichen kennzeichnet eine Korrektur.",
      savedAs: "Gespeichert als {{hours}} Std. (auf die nächsten 15 Minuten aufgerundet).",
      notes: "Notizen",
      correctionNeedsNote: "Eine Korrektur (negative Zeit) erfordert eine Notiz.",
      saveFailed: "Der Eintrag konnte nicht gespeichert werden.",
    },
    entries: {
      title: "Zeiteinträge — {{project}}",
      none: "Keine Einträge in diesem Zeitraum.",
      who: "Wer / Modell",
      hours: "Std.",
      invoiced: "Abgerechnet",
      invoicedHelp: "Dieser Eintrag ist Teil einer Abrechnung und kann nicht mehr geändert werden. Fügen Sie eine Korrektur in einem späteren Zeitraum hinzu.",
      confirmDelete: "Diesen Eintrag löschen?",
      deleteFailed: "Der Eintrag konnte nicht gelöscht werden.",
      loadFailed: "Die Einträge konnten nicht geladen werden.",
    },
    timer: {
      title: "Timer",
      idle: "Es läuft kein Timer. Drücken Sie die Uhr bei einem Projekt oder ▶ in einer Zeile, um einen zu starten.",
      startTitle: "Timer starten",
      chooseLine: "Zeile",
      startHelp: "Jeder Start und Stopp speichert einen eigenen Eintrag, so können Sie mehrere Zeiträume an einem Tag erfassen.",
      runningHere: "Der Timer läuft für dieses Projekt.",
      start: "Timer starten",
      stop: "Stoppen und speichern",
      alreadyRunning: "Es läuft bereits ein Timer.",
      willSave: "Wird als {{hours}} Std. gespeichert.",
      autoNotes: "Agentenläufe während dieses Timers",
      autoUnavailable: "Agentenläufe werden noch nicht aufgezeichnet.",
      noRuns: "Noch keine Agentenläufe.",
      refresh: "Agentenläufe aktualisieren",
      manualNotes: "Ihre Notizen",
      manualPlaceholder: "z. B. 15-minütiges Telefonat mit dem Kunden zum Audit-Scope",
      notesFailed: "Die Agentenläufe konnten nicht geladen werden.",
      stopFailed: "Der Timer konnte nicht gestoppt werden.",
      pause: "Pausieren",
      resume: "Fortsetzen",
      paused: "Pausiert – diese Zeit wird nicht abgerechnet.",
      pauseFailed: "Der Timer konnte nicht pausiert oder fortgesetzt werden.",
    },
    statement: {
      title: "Abrechnung erstellen",
      description: "Erstellen Sie eine Abrechnung für einen Kunden und einen Abrechnungszeitraum.",
      customer: "Kunde",
      noBilledProjects: "Dieser Kunde hat noch keine abgerechneten Projekte",
      cycle: "Abrechnungszyklus",
      cycleWeekly: "Wöchentlich",
      cycleBiweekly: "Zweiwöchentlich",
      periodStart: "Beginn des Zeitraums",
      periodEnd: "Ende des Zeitraums",
      periodNote: "Es wird nur abgerechnete, nicht gesperrte Zeit dieses Zeitraums einbezogen. Erzeugte Einträge werden schreibgeschützt.",
      generate: "Abrechnung erstellen",
      working: "Wird verarbeitet…",
      created: "Abrechnung {{name}} erstellt",
      number: "Abrechnungsnummer",
      total: "Summe",
      entries: "Einträge",
      downloadPdf: "PDF herunterladen",
      periodFailed: "Der vorgeschlagene Zeitraum konnte nicht geladen werden",
      cycleFailed: "Der Abrechnungszyklus konnte nicht geändert werden",
      generateFailed: "Die Abrechnung konnte nicht erstellt werden",
      downloadFailed: "Das PDF konnte nicht heruntergeladen werden",
    },
    rates: {
      title: "Projektsätze",
      unchanged: "Leer lassen, um beizubehalten",
      useDefault: "Firmenstandard verwenden",
      help: "Neue Einträge verwenden den geltenden Satz. Bestehende Einträge behalten den Satz, mit dem sie erstellt wurden.",
      companyDefault: "Firmenstandard",
      saved: "Gespeichert. Entwickler: {{developer}} · Ressource: {{resource}}",
      saveFailed: "Die Sätze konnten nicht gespeichert werden.",
    },
    settings: {
      title: "Zeit und Abrechnung",
      description: "Standard-Stundensätze und die Bankverbindung, die auf Kundenabrechnungen gedruckt wird.",
      developerRate: "Satz Entwickler ($/Std.)",
      resourceRate: "Satz Ressource ($/Std.)",
      ratesHelp: "Ein Projekt kann jeden der beiden Sätze überschreiben. Eine Änderung des Satzes ändert bestehende Einträge nicht.",
      achTitle: "ACH-Zahlungsdaten",
      achComplete: "Vollständig",
      achIncomplete: "Unvollständig — Abrechnungen weisen darauf hin, dass ACH nicht eingerichtet ist",
      bankName: "Name der Bank",
      accountName: "Kontoinhaber",
      routingNumber: "Routingnummer",
      accountNumber: "Kontonummer",
      endingIn: "endet auf {{last4}}",
      notSet: "nicht festgelegt",
      replacePlaceholder: "Geben Sie eine neue Nummer ein, um sie zu ersetzen",
      achHelp: "Verschlüsselt gespeichert. Hier werden nur die letzten 4 Ziffern angezeigt; die vollständigen Nummern erscheinen nur auf Abrechnungen.",
      nothingChanged: "Nichts zu speichern.",
      saved: "Gespeichert.",
      saveFailed: "Die Einstellungen konnten nicht gespeichert werden.",
      loadFailed: "Die Einstellungen konnten nicht geladen werden.",
    },
  },
};
